import { AppContext } from "App";

import { areFiltersEqual, ColumnAggregationFunctions, ColumnDefinition, ColumnType, Filter } from "@multiweb/aggregationRow";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import {
    AggregationRowConnector,
    IAggregationRowEvents,
    IColumnAddedEventData,
    IColumnDefinitionsChangedEventData,
    IColumnRemovedEventData,
    IColumnWidthChangedEventData,
    IFilterChangedEventData,
    IOrderOfColumnsChangedEventData,
} from "@multiweb/reactConnectors/AggregationRowConnector";
import { DIContainer } from "Global/Base/DIContainer";
import { BasicDataType } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { IRecordIdentification } from "OW.Remoting/Remoting/DataTypes/RecordIdentification";
import { ErrorHandler } from "UI/Errors";
import { SelectionChangedPreviewEventArgs } from "UI/UI.Controls/DataGridInterfaces";
import { DataGridSharedMethods } from "UI/UI.Controls/DataGridSharedMethods";
import { MEDataSource, MULTI_OBJECT_ID } from "UI/UI.Controls/DataSource/MultiEdit";
import { IMultiEditObject } from "UI/UI.Controls/DataSource/MultiEdit/MEOWObject";
import { EditableDataGrid } from "UI/UI.Controls/EditableDatagrid";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { AdditionalOrdering, IDataSourceExtended, IMEDataSource, IOrderBy, OrderType } from "../../../../../Global/Base/DataSources/Interfaces";
import { IAttrStringValue } from "../../../../../Global/Base/DataTypes/IAttrValue";
import { Event as GlobalEvent, EventAwaiter, EventCallback } from "../../../../../Global/Base/Events";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { CancelError, CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { Deferred, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { RightType } from "../../../../../Global/Base/Rights/RightTypes";
import { EnumUtils } from "../../../../../Global/Base/Utils/EnumUtils";
import { ObjectUtils } from "../../../../../Global/Base/Utils/ObjectUtils";
import { StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { EOWRecordPermissionException } from "../../../../../OW.Remoting/Basic/OWExceptions";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IWorkflowMenuItem } from "../../../../../OW.Remoting/Remoting/IWorkflowManager";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { IAwaitableEvent } from "../../../../UI.BaseControls/AwaitableEvent";
import { IBeforeSaveAwaitable, IValidatable } from "../../../../UI.BaseControls/ControlInterfaces";
import { EventHelper } from "../../../../UI.BaseControls/Helpers/EventHelper";
import { IBooleanDetailValueEvent } from "../../../../UI.BaseControls/IEvent";
import { ActionType, IAction } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { CompactDataGrid } from "../../../../UI.Controls/CompactDatagrid";
import { Column } from "../../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { CompactDataRow } from "../../../../UI.Controls/DataGridSubcontrols/Components/CompactDataRow";
import { IDoubleClickEventArgs } from "../../../../UI.Controls/DataGridSubcontrols/Components/DataRow";
import { IDataRowElement, IEditableDataRowElement, IEditableListDataRowElement } from "../../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { HeaderTemplate } from "../../../../UI.Controls/DataGridSubcontrols/Templates/HeaderTemplate";
import { EditableDBDataSource, IActualObjectChangeEventArgs, SaveResultData } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { GroupingTreeViewDataSource } from "../../../../UI.Controls/DataSource/GroupingTreeViewDataSource";
import { DropDownMenuHorizontalOpen } from "../../../../UI.Controls/DropDownMenu/DropDownInterfaces";
import { DropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { SeparatorMenuItem } from "../../../../UI.Controls/DropDownMenu/SeparatorItem";
import { GroupingTreeViewGrid } from "../../../../UI.Controls/GroupingTreeViewGrid";
import { IDataGrid, IEditableListDataGrid, IListDataGrid } from "../../../../UI.Controls/IDataGrid";
import { IDataGridSelectionChangedEvent, IGroupingDataGridSelectionChangedEventData } from "../../../../UI.Controls/IEvent";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ControlRightTypesGroup, RightGroups } from "../../../Rights/RightGroups";
import { Control } from "../../Controls/Control";
import { FormControlParams, IAttributeControl } from "../../Controls/ControlInterfaces";
import { ControlUtils } from "../../Controls/ControlUtils";
import { ColumnInfoBuilder } from "../../DataBuilders/ColumnInfoBuilder";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { DataSourceErrorHandler } from "../../DataSourceErrorHandler";
import { FormUICustomProperty, Icon } from "../../FormUI/FormUI";
import { MenuUIExtensionProperties } from "../../FormUI/FormUIShared";
import { ISystemOperationRights, SystemOperation } from "../../Helpers/SystemOperationRights";
import { FrameColumnInfoAttributes, IFrameProfileInfo } from "../../IFrameColumnInfo";
import { MessageButtonType, MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { WindowState } from "../../UIManagerInterfaces";
import { UploadManager } from "../../UploadManager";
import { OpenModeType } from "../Actions/DetailFormAction";
import { StandardAction } from "../Actions/StandardAction";
import { DetailFrame } from "../DetailFrame";
import { FilterBlock } from "../Filters/FilterBlock";
import { FilterManager, IFilterExecutedEventArgs } from "../Filters/FilterManager";
import { FormResult } from "../Forms/FormResult";
import { FrameSelChanged, IActualObjectChangedEventArgs } from "../Frames";
import { IExtendedLifeCycle } from "../IExtendedLifeCycle";
import { IAggregableListFrame } from "../IFrame";
import { ObjectCheckingResult, SharedMethods } from "../SharedMethods";
import { EditStyleType, IFulltextSearchableFrame, IFulltextSearchCondition, IMasterableFrame } from "./FrameInterfaces";
import { FrameSharedMethods } from "./FrameSharedMethods";
import { GenericListFrame, OpenModalDetailOption } from "./GenericListFrame";
import { ISeparateDetailFrame } from "./IDetailMasterableFrame";
import { ListFrameRightsEvaluationOverrides } from "./ListFrameRightsEvaluationOverrides";
import { MasterableFrame } from "./MasterableFrame";

const logger = LazyLoadingSectionLogger.create(LoggingSection.listFrame);

export class ListFrame extends GenericListFrame implements IFulltextSearchableFrame, IExtendedLifeCycle, IAggregableListFrame {
    //#region Events
    /** Událost vyvolávaná při spuštění libovolného filtru */
    public readonly filterExecuted: GlobalEvent<ListFrame, void> = new GlobalEvent<ListFrame, void>();
    /**
     * Událost vyvolaná při změně šířky sloupce.
     */
    public readonly columnWidthChanged: GlobalEvent<ListFrame, IColumnWidthChangedEventData> = new GlobalEvent<ListFrame, IColumnWidthChangedEventData>();
    /**
     * Událost vyvolaná při změně pořadí sloupců.
     */
    public readonly orderOfColumnsChanged: GlobalEvent<ListFrame, IOrderOfColumnsChangedEventData> = new GlobalEvent<ListFrame, IOrderOfColumnsChangedEventData>();
    /**
     * Událost vyvolaná při změně definic sloupců.
     */
    public readonly columnDefinitionsChanged: GlobalEvent<ListFrame, IColumnDefinitionsChangedEventData> = new GlobalEvent<ListFrame, IColumnDefinitionsChangedEventData>();
    /**
     * Událost přidání/zviditelnění sloupce.
     */
    public readonly columnAdded: GlobalEvent<ListFrame, IColumnAddedEventData> = new GlobalEvent<ListFrame, IColumnAddedEventData>();
    /**
     * Událost odebrání/skrytí sloupce.
     */
    public readonly columnRemoved: GlobalEvent<ListFrame, IColumnRemovedEventData> = new GlobalEvent<ListFrame, IColumnRemovedEventData>();
    /**
     * Událost změny filtru.
     */
    public readonly filterChanged: GlobalEvent<ListFrame, IFilterChangedEventData> = new GlobalEvent<ListFrame, IFilterChangedEventData>();
    /**
     * Událost vyvolaná při změně záznamů. Může se jednat o aktualizaci záznamů, přidání, úpravu, smazání záznamu.
     */
    public readonly itemsRefreshed: GlobalEvent<ListFrame, void> = new GlobalEvent<ListFrame, void>();

    //#endregion Events

    //#region Fields
    /**
     * Klíč sloupce pro sloupec s kontrolními prvky. Hodnota převzata z HeaderRow.ts, kde je sloupec napevno definovaný.
     */
    private static readonly _CONTROL_COLUMN_ID = "sl0";
    private static readonly _SHOW_AGGREGATION_ROW_LOC_KEY = "GUI-709515"; // Zobrazit agregační funkce
    private static readonly _HIDE_AGGREGATION_ROW_LOC_KEY = "GUI-709516"; //Skrýt agregační funkce
    /** Seznam aktivních akcí, řízených přes enableInEdit */
    protected _enabledInEditActions: Control[];
    /** Udržuje informaci o aktuálně použitém filtru (pro třídící strom) */
    private __filterCondition: string;
    protected override __rightsEvaluationOverrides: ListFrameRightsEvaluationOverrides;
    /** Určuje, zda je detail otevírán do nového okna (true) nebo jen modálu (false) */
    private _showOnMainForm: boolean;
    /** Statická metoda spouštěná po uložení při nastavení esMulti */
    private _multiEditAfterMethod: string;
    /** Atribut nastavovaný na true před uložením záznamu při nastavení esMulti */
    private _multiEditFlagAttr: string;
    /**
     * Indikátor, zda se má po vytvoření ListFrame automaticky zobrazit patička s agregačním řádkem.
     */
    private _showAggregationRow: boolean;
    /**
     * Akce v menu seznamu na zobrazení/skrytí agregačního řádku.
     *
     * Pozn. Musí to být jako field, aby se k tomu dostala např. i {@link hideAggregationFooter}, která je volána i odjinud, než jen z menu.
     */
    private _aggregationRowAction: StandardAction;
    /**
     * Vypnutí zkracování hodnot. Nastavením na true, klient při dotazu na data, kdy je třeba znát skutečnou nezkrácenou hodnotu, načítá hodnoty bez zkracování.
     */
    private _distinctFullLength: boolean;
    /**
     * Promise pro probíhající ukládání
     */
    protected _recordsSavedDeferred: Deferred<boolean>;
    //#endregion Fields

    //#region Events
    //#endregion Events

    //#region Listeners

    /**
    * Master object change listener nebo maloobchodni cena ...
    */
    private _isMultiEditingChangedListener: (sender: IMEDataSource, value: boolean) => void;
    private _selectionChangedListener: (sender: MasterableFrame, e: FrameSelChanged) => Promise<void>;
    /** Listener pro změnu záznamu v grupovacím stromu */
    private _groupSelectionChangedListener: EventListener;
    private _newObjectIdChangedListener: EventCallback;
    private _addObjectChangedListener: EventCallback;
    private _isLovFrame: boolean | undefined = undefined;

    /**
     * Chain promisů s probíhajícími změnami zvolené položky LF. V některých případech se aplikace nechová korektně při rychlých "paralelních" změnách vybraných položek LF, proto bylo nutné tyto změny zřetězit.
     */
    private _waitForSelectionChangedTask: Promise<void> = Promise.resolve();

    /**
     * Listener pro změnu profilu.
     * Po změně profilu se vyvolá událost pro změnu definic sloupců agregačního řádku.
     * @private
     */
    private async _selectedProfileChangedListener(): Promise<void> {
        if (this.dataGrid) {
            const columnDefinitions = await this._getColumnDefinitions();
            const orderOfColumns = this._getOrderOfColumns();
            const numberOfFrozenColumns = this.dataGrid.frozenColumnCount + 1; // +1 kvůli control column
            this.columnDefinitionsChanged.trigger(this, { columnDefinitions, orderOfColumns, numberOfFrozenColumns });
        }
    }
    //#endregion Listeners

    //#region Properties
    private get _filterCondition(): string {
        return this.__filterCondition;
    }
    private set _filterCondition(v: string) {
        this.__filterCondition = v;
    }
    /**
     * Značí, zda se editace záznamu má provést na oteřeném modálním detailu
     */
    protected get _shouldOpenDetailInModal(): boolean {
        // musí být splněno několik podmínek:
        // 1) není v režimu readonly (to v podstatě znamená, že už jsme v modálním okně a tento seznam je na nadřízeném formu)
        // 2) je vyplněna property DetailForm
        // 3) na formuláři neexistuje detail nebo ten detail nepatří k tomuto framu
        return this.interactive && this.detailForm != null && (this.ownerForm.detailFrame == null || this.ownerForm.detailFrame.masterFrame != this);
    }

    /**
     * Otevře detail do nového okna nebo modálu
     * @param openInEdit Zda má být otevřen v režimu editace
     * @param objID ID otevíraného objektu
     * @param showOnMainForm Zda má být otevřen do nového okna
     */
    protected override async _openDetailSeparately(opt: OpenModalDetailOption = {}): Promise<void> {
        opt.showOnMainForm ||= this._showOnMainForm;
        opt.openInMultiEdit = this.isMultiSelectingMode;

        await super._openDetailSeparately(opt);
    }

    public get isLovFrame(): boolean {
        return this._isLovFrame;
    }

    public override get visible(): boolean {
        return super.visible;
    }

    public override set visible(value: boolean) {
        if (this._permanentInvisible)
            return;
        const oldVisible = super.visible;
        super.visible = value;
        if (value && value !== oldVisible && this._pendingMasterObjectChange) {
            this._onMasterObjectChanged({ actualObject: (this.masterFrame ? this.masterFrame.actualObject : null), previousActualObject: null });
        }
    }

    public get permanentInvisible(): boolean {
        return this._permanentInvisible;
    }

    public set permanentInvisible(value: boolean) {
        this._permanentInvisible = value;
    }

    /**
     * Vrací atributy, které jsou definici určeny pro fulltextové vyhledávání
     */
    public get fulltextAttributes(): string[] {
        return this._fulltextAttributes;
    }

    /**
     * Podmínka pro fulltextové vyhledávání
     */
    public get fulltextCondition(): IFulltextSearchCondition {
        return this._fulltextCondition;
    }

    public set fulltextCondition(value: IFulltextSearchCondition) {
        this._fulltextCondition = value;
        this.dataSource.fulltextCondition = value;
    }

    /**
     * Označuje frame, který má být použit pro výběr do LOV (má si registrovat akci ActionType.standardSelectRecord)
     */
    public get isMainSelectionFrame(): boolean | undefined {
        return this._isMainSelectionFrame;
    }

    public set isMainSelectionFrame(value: boolean | undefined) {
        this._isMainSelectionFrame = value;
    }
    //#endregion Properties

    //#region Fields

    public override dataSource: MEDataSource;

    /**Podminka vytazena z definice seznamu*/
    public override condition: string;

    /** Určuje, zda je povoleno seskupování dat */
    public disableGrouping: boolean = false;

    private _fulltextCondition: IFulltextSearchCondition = {};
    private _isMainSelectionFrame: boolean | undefined;

    public override dataGrid: IEditableListDataGrid<IEditableListDataRowElement>;
    public override supportsMultiEditing = true;
    public override compactDataGrid: CompactDataGrid<CompactDataRow>;

    public override get isChanged(): boolean {
        const hasEditedRows = (this.dataGrid && this.dataGrid.editedRows.length > 0) || false;

        if (this.actualObject)
            return super.isChanged || !this.actualObject.isValid || hasEditedRows;

        return super.isChanged || hasEditedRows;
    }

    public override set isChanged(val: boolean) {
        super.isChanged = val;
    }

    /**
     * kod formulare, ktery se otevira pro detail zaznamu
     */
    public override detailForm: string;

    /**
     * seznam nativnich udalosti
     */
    private _eventFunction: any = {};

    //private Grouping: boolean;
    public override set headerColor(value: string) {
        if (this._color && this._color.length > 0)
            this.element.classList.remove(this._color);

        this.element.classList.add(value);

        this._color = value;

        if (this.dataGrid && this.dataGrid.headerRow)
            this.dataGrid.headerColor = value;
        if (this.compactDataGrid && this.compactDataGrid.headerRow)
            this.compactDataGrid.headerColor = value;

        this.menu.setProperty(FormUICustomProperty.pageColor, value);
    }

    public override get headerColor(): string {
        return this._color; //this.dataGrid.Header.Color;
    }

    private _filterMenu: DropDownMenu;
    private _showFilterValues: boolean = false;
    public get showFilterValues(): boolean {
        return this._showFilterValues;
    }
    public set showFilterValues(value: boolean) {
        if (value) {
            this._showFilterValues = true;
        }
    }

    /**
     * Jmeno vazby v objektu na nadrizenem framu
     */
    public override masterLinkToAttr: string;

    public override get masterFrame(): IMasterableFrame {
        return this._masterFrame;
    }

    public override set masterFrame(masterFrame: IMasterableFrame) {
        if (this.masterFrame) {
            this.masterFrame.selectionChanged.remove(this._selectionChangedListener);
        }
        super.masterFrame = masterFrame;

        if (this.masterFrame == null || this.masterFrame.selectionChanged == null)
            return;

        this.masterFrame.selectionChanged.add(this._selectionChangedListener = async () => {
            if (!this.canValueLoad)
                return;

            //this.checkRights(); //TODO-rites: old rights
            await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.selectionChanged);
        });
    }

    public override get detail(): DetailFrame {
        return this._detail;
    }

    public override set detail(detailFrame: DetailFrame) {
        super.detail = detailFrame;
        if (this.detail) {
            if (this.onEditMethod != undefined && this.detail.onEditMethod == undefined) {
                this.detail.onEditMethod = this.onEditMethod;
            }
            if (this.onCancelMethod != undefined && this.detail.onCancelMethod == undefined) {
                this.detail.onCancelMethod = this.onCancelMethod;
            }
        }
    }

    /**
     * Aktuálně vybrané atributy podle kterých se grupuje
     */
    private _currentAttributesToGroup?: string[];
    public get currentAttributesToGroup(): string[] {
        return this._currentAttributesToGroup;
    }
    public set currentAttributesToGroup(attrsToGroup: string[]) {
        this._currentAttributesToGroup = attrsToGroup;
    }

    private _gridStyle: ListFrameGridStyle = ListFrameGridStyle.gsauto;

    /**
     * Vyjadřuje zda došlo ke změně nadřízeného objektu během doby, kdy frame nebyl vidět (tato změna se provede po jeho zaktivnění)
     */
    private _pendingMasterObjectChange: boolean = false;
    private _filterClearedListener: any;
    private _filterChangedListener: EventCallback<FilterManager, FilterBlock>;
    private _canValueLoadChangedListener: any;
    private _windowResizedListener: any;
    private _masterObjectAttrsChangedListener: (sender: OWObject, attributeNames: string[]) => Promise<void>;
    private _filterAction: StandardAction;
    private _filterListAction: StandardAction;
    private _previousActualObject: OWObject;
    private _previousMasterObject: OWObject;
    private _groupGrid: GroupingTreeViewGrid;
    private _groupColumns: Array<string>;
    private _groupWidth: number = 300;
    private _componentsElement: HTMLElement;

    /**
     * Connector pro agregační řádek, který je možné zobrazit v patičce.
     */
    private _aggregationRowConnector?: AggregationRowConnector;

    /**
     * Predikáty, které se vyhodnocují pro zjištění editovatelnosti.
     * Lze je nastavovat metodami addCanEditPredicate, containsCanEditPredicate, removeCanEditPredicate
     */
    private _canEditPredicates: ((...params: any[]) => boolean)[] = [];

    private _fulltextAttributes: string[] = [];

    /**
     * Aktuální filtr používaný agregačním řádkem. Slouží pro detekci změn filtru.
     * @private
     */
    private _aggregationRowFilter: Filter | undefined;
    //#endregion Fields

    //#region Constructors
    constructor(params: FormControlParams) {
        super({ ...params, logger });

        /**
         * V konstruktoru framu může být jen jednoduchá inicializace hodnot (z definice a pod.)
         * Logika a asynchronní procesy musí být až v inicializační metodě (initialize()),
         * která je vyvolávaná v potřebném pořadí a dá se na její doběhnutí čekat
         */
        this.fontColorFlags = this.element.getAttribute("data-FontColorFlags");
        this.fontColorAttr = this.element.getAttribute("data-FontColorAttr");
        const fulltextAttributesString = this.getDataAttr("FulltextAttrs");
        if (fulltextAttributesString) {
            this._fulltextAttributes = fulltextAttributesString.split(";");
        }
        this._showFilterValues = DataConverter.toBoolean(this.element.getAttribute("data-ShowFilterValues"), false);

        const gStyle = this.element.getAttribute("data-gridstyle");
        if (gStyle != null)
            this._gridStyle = EnumUtils.getEnumValueFromName(ListFrameGridStyle, gStyle.toLowerCase());

        this._componentsElement = createElement({ classes: "inner-components" });

        this.container.appendChild(this._componentsElement);

        const defaultGroup = this.element.getAttribute("data-defaultGroup");

        if (defaultGroup) {
            this._groupColumns = [];

            defaultGroup.split(";").forEach((attribute) => this._groupColumns.push(attribute));
            this.currentAttributesToGroup = this._groupColumns;

            const groupWidth = this.element.getAttribute("data-groupWidth");

            if (groupWidth) {
                this._groupWidth = DataConverter.toNumber(groupWidth);
            }
        }

        this.onEditMethod = this.element.getAttribute("data-oneditmethod");
        this.onCancelMethod = this.element.getAttribute("data-oncancelmethod");
        this._showOnMainForm = this.getBoolDataAttr("showOnMainForm");
        this._multiEditAfterMethod = this.getDataAttr("multiEditAfterMethod");
        this._multiEditFlagAttr = this.getDataAttr("multiEditFlagAttr");

        if (this.onEditMethod)
            this.editStyle = EditStyleType.esSingle; // Při nastavené onEditMethod nemůžu mít multi editaci na seznamu

        this._showAggregationRow = this.element.getAttribute("data-AggregationRow")?.toLowerCase() === "true";
        this.selectedProfileChanged.add(this._selectedProfileChangedListener.bind(this));
        this._distinctFullLength = DataConverter.toBoolean(this.element.getAttribute("data-distinctfulllength"));
        this._allowExport = DataConverter.toBoolean(this.element.getAttribute("data-exportable"), true);
    }
    //#endregion Constructors

    //#region Methods
    protected override _initializeVisual(): Promise<void> {
        this._initLovFrameProperty();

        return super._initializeVisual();
    }

    private _initLovFrameProperty(): void {
        if (!this.ownerForm.isLov) this._isLovFrame = false;
        else if (StringUtils.isNullOrEmpty(this.ownerForm.lovFrame)) this._isLovFrame = this.isMainFrame || this.isMainSelectionFrame;
        else this._isLovFrame = StringUtils.compareCaseInsensitive(this.ownerForm.lovFrame, this.frameName);
    }

    protected override _initializeListeners(): void {
        super._initializeListeners();

        this._events.add(this.actualObjectSaved, async (_sender, _obj) => {
            this._enabledInEditActions = null; // Mohlo dojít ke změně aktivních akcí podle podmínek, je třeba je znovu načíst

            await this._getWFItems();
        });

        // data se načítají až po vytvoření datagridu
        this._events.add(this.dataGridInitialized, () => this._onDataGridInitialized());

        if (!this.masterFrame) {
            return;
        }

        // #region NewObjectIdChanged
        this._newObjectIdChangedListener = (obj: OWObject, atrName: string) => {
            if (atrName === "ID") {
                this.updateCondition(this.dataSource); // úprava podmínky je potřeba jen při přiřazení ID novému objektu
                obj.attributeChanged.remove(this._newObjectIdChangedListener); // dále již nepotřebujeme
            }
        };

        this._addObjectChangedListener = () => {
            if ((this.masterFrame.actualObject) && (this.masterFrame.actualObject.isNew)) {
                this.masterFrame.actualObject.attributeChanged.add(this._newObjectIdChangedListener);
                this.masterFrame.actualObjectChanged.remove(this._addObjectChangedListener);
            }
        };

        // na vytvoření nového záznamu na masteru pověsíme handler, který bude čekat na přiřazení ID a potom podle něj upraví podmínku
        this.masterFrame.actualObjectChanged.add(this._addObjectChangedListener);

        //Pri otevirani detailu v modalnim okne je potreba konrolovat jestli neni nahodou masterObject new, protoze v tomto pripade se nepusti this.masterFrame.actualObjectChanged
        if (this.masterFrame.actualObject && this.masterFrame.actualObject.isNew)
            this.masterFrame.actualObject.attributeChanged.add(this._newObjectIdChangedListener);
        // #region NewObjectIdChanged
    }

    protected async _onDataGridInitialized(): Promise<void> {
        // Na tento await spoléhá TLF
        await this.waitForDataLoad();

        if (!this.masterFrame) {
            if (this._groupGrid) {
                this._loadGroupTreeData(this.ownerForm.queryMode);
            }
            return;
        }

        // pokud už má master frame načtený objekt, tak se o tom vyvolá oznámení
        if (this.masterFrame.actualObject != null) {
            this._pendingMasterObjectChange = true;
            this._onMasterObjectChanged();
        }

        this.masterFrame.actualObjectChanged.add(async () => {
            this._pendingMasterObjectChange = true;
            if (this.masterFrame) {
                await this.masterFrame.rightsEvaluationTask;
            }
        });
    }

    protected override async _initializeDataSource(): Promise<void> {
        this.dataSource = new MEDataSource({
            className: this.objectName,
            readVersioned: this.readVersioned,
            frameContext: this.frameContext,
            maxRecordCount: undefined,
            composites: this.composites,
        });

        this.dataSource.isMultiEditingChanged.add(this._isMultiEditingChangedListener = (_sender, value) => {
            this._isMultiEditing = value;
        });

        const objectClass = await this._getClassDescription();

        if (this.orderBy)
            for (const order of this.orderBy) {
                const attrType = (await objectClass.getAttributeInfo(order.attributeName)).basicType;
                if (attrType == BasicDataType.String || attrType == BasicDataType.StrNum)
                    order.additionalOrdering = AdditionalOrdering.linguistic;
            }

        this.dataSource.orderBy = this.orderBy;

        this.dataSource.error.add((_sender, error) => {
            DataSourceErrorHandler.handleDataSourceError(error, this.dataSource.hasFilterCondition);
        });
        this.dataSource.attributesChanged.add(async (_senderDataSource: EditableDBDataSource, eventData: IActualObjectChangeEventArgs) => {
            if (this.isEditing) {
                if (eventData.isChanged) {
                    await this.createContext();
                }

                this.isChanged = eventData.isChanged;
            }

            //pokud byla data právě dešifrována a týkají se AO, je potřeba přehodnotit práva.
            if (eventData.isDecrypted && this.actualObject && this.actualObject.id == (AttrValueReader.read(eventData.key))) {
                //znovu vyhodnotí práva protože, AO byl právě dešifrován
                await this.actualObject.refresh();
                const action = this.menu.getAction(ActionType.standardDecrypt);
                if (action)
                    action.enabled = false;
            }
            /**Zavolám throttled metodu pro vyhodnocení práv.
             * Pokud by docházelo k časté změně hodnot atributů spustí se vyhodnocení práv maximálně jednou za 500 ms.*/
            this._evaluateAttributeChangedRightsThrottledWithContext(eventData.context ?? this.actualObject);
            /**Na throttled metodu nelze čekat pomocí await při při jejím volání.
             * Pokud by bylo potřeba přidat nějaký kód za předchozí volání, který se má provést až po doběhnutí vyhodocení práv,
             * bude potřeba implementovat čekání pomocí odloženého promise. */
        });
        this.dataSource.onObjectInstanceChanged.add((_sender, value) => {
            // funguje opravdu jen pro změnu instance a ne záznamu - k tomu jsou jiné mechanizmy a toto pak dělá duplicitní činnost (ono celkově je to takové divné - changeset 25137)
            if (this.actualObject && value && this.actualObject.id === value.id && this.actualObject.instanceID !== value.instanceID)
                this._setActualObject(value);
        });

        if (this.editStyle === EditStyleType.esMulti) {
            if (this._multiEditFlagAttr) {
                this.dataSource.beforeSave.add(async (_cancelableEventArgs, _sender, eventData) => {
                    await PromiseUtils.for(0, eventData.objects.length, async (index) => {
                        await eventData.objects[index].setValue(this._multiEditFlagAttr, true);
                    });
                });
            }

            if (this._multiEditAfterMethod) {
                this.dataSource.afterSaved.add(async (_sender, eventData) => {
                    if (eventData.saved.length === 0) {
                        return;
                    }

                    const { objectName, methodName } = FrameSharedMethods.parseStaticMethod(this, this._multiEditAfterMethod);

                    try {
                        UIManager.instance.showLoader();

                        const methodResult = await OWManager.runStaticMethod({
                            className: objectName,
                            methodName,
                            requestContext: this._getRequestContext(),
                        });

                        if (eventData.saved.includes(this.actualObject.id.toString())) {
                            this.refreshActualObject();
                        }

                        UIManager.instance.showResultMessages(methodResult.messages);
                    } finally {
                        UIManager.instance.hideLoader();
                    }
                });
            }
        }

        this.ownerForm.onDoOpen.add(async () => {
            if (this.ownerForm.selectedId && this.currentDataGrid /* && this.currentDataGrid.dataRows.length > 0*/) {
                this.currentDataGrid.clearSelectedRows();
                const selectedId = this.ownerForm.selectedId;
                this.ownerForm.selectedId = undefined;
                await this.loadAndSelectRecord(selectedId);
            }
        });

        await this.updateCondition(this.dataSource);

        // aktualizace záznamů uložením nového
        this.dataSource.afterSaved.add(() => {
            this.itemsRefreshed.trigger(this);
        });
        // aktualizace záznamů smazáním
        this.dataSource.deleted.add(() => {
            this.itemsRefreshed.trigger(this);
        });
        this.dataSource.loadingStarted.add((_dataSource, startIndex) => {
            if (startIndex === 0) {
                // Pokud je filtr aktuální, vyšle se událost itemsRefreshed, jinak se místo ní vyšle filterUpdated událost.
                if (!this._triggerFilterUpdatedIfNeeded()) {
                    this.itemsRefreshed.trigger(this);
                }
            }
        });

        this.dataSource.loadingEnded.add(() => {
            this._triggerComputedChoiceCellWidthChanged();
        });
    }

    /**
     * Ke standardním položkám menu přidá položku pro přepínání patičky s agregačnímřádkem.
     * @private
     */
    protected override async _addFrameChoiceActions(): Promise<void> {
        await super._addFrameChoiceActions();
        this._aggregationRowAction = this._createAggregationRowAction();
        this._updateAggregationRowAction(this.dataGrid?.emptyMessage?.visible);
    }

    private _triggerComputedChoiceCellWidthChanged(newControlWidth?: number): void {
        if (this.dataGrid) {
            this.columnWidthChanged.trigger(this, {
                columnKey: ListFrame._CONTROL_COLUMN_ID,
                width: newControlWidth || this.dataGrid.computeChoiceCellWidth(),
            });
        }
    }

    /**
     * Vytvoří a vrátí akci přepínající zobrazení agregačního řádku (zobrazit/skrýt).
     */
    private _createAggregationRowAction(): StandardAction {
        const aggregationRowAction = FrameSharedMethods.createStandardAction(ActionType.standardToggleAggregationRow, async () => {
            if (this._aggregationRowConnector) {
                this.hideAggregationFooter();
            } else {
                await this.showAggregationFooter();
            }
        }, this);
        aggregationRowAction.locKey = this._aggregationRowConnector ? ListFrame._HIDE_AGGREGATION_ROW_LOC_KEY : ListFrame._SHOW_AGGREGATION_ROW_LOC_KEY;
        aggregationRowAction.toMenu = MenuType.mtFrameMenu;

        return aggregationRowAction;
    }

    /**
     * @internal
     */
    protected override set _columnInfoBuilder(columnInfoBuilder: ColumnInfoBuilder) {
        super._columnInfoBuilder = columnInfoBuilder;
        columnInfoBuilder.columnPositionChanged.add(() => {
            this.orderOfColumnsChanged.trigger(this, { orderOfColumns: this._getOrderOfColumns() });
        });
        columnInfoBuilder.columnWidthChanged.add((_sender, { columnId, width }) => {
            this.columnWidthChanged.trigger(this, { columnKey: columnId, width });
        });
    }

    /**
     * Získá definice sloupců pro agregační řádek.
     */
    private async _getColumnDefinitions(): Promise<ReadonlyArray<ColumnDefinition>> {
        const controlColumn = this._createControlColumnDefinition();
        const attributeNamesWithEncryptingAllowed = await this._getAttributeNamesWithEncryptingAllowed();
        const dataGridColumns = this.dataGrid.columns
            .filter((column) => column.visible)
            .map((column) => AggregationRowConnector.columnToColumnDefinition(column, attributeNamesWithEncryptingAllowed.includes(column.attrName)));
        return [controlColumn, ...dataGridColumns];
    }

    /**
     * Zobrazí patičku s agregačním řádkem.
     */
    public async showAggregationFooter(): Promise<void> {
        if (!this.dataGrid) {
            throw new Error("[ListFrame] DataGrid must be initialized before usage of footer.");
        }
        if (!this.dataSource) {
            throw new Error("[ListFrame] DataSource must be initialized before usage of footer.");
        }

        const footerContent = document.createElement("div");
        const className = this.dataSource.className;
        const aggregationRowKey = `${this.ownerForm.formInstanceId}-${this.frameName}-${className}`;
        const columnDefinitions = await this._getColumnDefinitions();
        const orderOfColumns = this._getOrderOfColumns();
        const numberOfFrozenColumns = this.dataGrid.frozenColumnCount + 1; // +1 kvůli control column
        const filter = this._createFilter();
        const events: IAggregationRowEvents = {
            columnWidthChangedEvent: this.columnWidthChanged,
            orderOfColumnsChangedEvent: this.orderOfColumnsChanged,
            columnDefinitionsChangedEvent: this.columnDefinitionsChanged,
            columnAddedEvent: this.columnAdded,
            columnRemovedEvent: this.columnRemoved,
            filterChangedEvent: this.filterChanged,
            refreshEvent: this.itemsRefreshed,
        };

        this._aggregationRowConnector = new AggregationRowConnector(footerContent, aggregationRowKey, className, columnDefinitions, orderOfColumns, numberOfFrozenColumns, filter, this.hideAggregationFooter.bind(this), events);
        this.dataGrid.setFooterContent(footerContent);
        this.dataGrid.setRenderFooter(true);
        this._aggregationRowAction.locKey = ListFrame._HIDE_AGGREGATION_ROW_LOC_KEY;
    }

    /**
     * @inheritDoc
     */
    public getColumnsAggregationFunctions(): ColumnAggregationFunctions {
        if (!this._aggregationRowConnector) {
            throw new Error("[ListFrame] Aggregation row is not initialized");
        }
        return this._aggregationRowConnector.getColumnsAggregationFunctions();
    }

    /**
     * @inheritDoc
     */
    public isAggregationRowVisible(): boolean {
        return !!this._aggregationRowConnector;
    }

    /**
     * @inheritDoc
     */
    public hasSelectedAggregationFunctions(): boolean {
        const columnAggregationFunctions = this.getColumnsAggregationFunctions();
        return !Object.values(columnAggregationFunctions).every(aggregationFunction => aggregationFunction === null);
    }

    /**
     * Vytvoří definici pro první sloupce obsahující kontrolní prvky.
     * Sloupec je napevno definovaný v HeaderRow.ts, odkud je převzat i klíč (id) sloupce.
     */
    private _createControlColumnDefinition(): ColumnDefinition {
        return {
            key: ListFrame._CONTROL_COLUMN_ID,
            columnType: ColumnType.control,
            width: this.dataGrid.computeChoiceCellWidth(),
        };
    }

    /**
     * Skryje patičku s agregačním řádkem a uvolní související zdroje.
     *
     * Pozn. Tato funkce je volána z různých míst:
     * - z menu - při kliku na {@link _aggregationRowAction}
     * - při kliku na křížek na agregačním řádku
     */
    public hideAggregationFooter(): void {
        if (!this.dataGrid) {
            throw new Error("[ListFrame] DataGrid must be initialized before usage of footer.");
        }
        this.dataGrid.setRenderFooter(false);
        this.dataGrid.setFooterContent(undefined);
        this._aggregationRowConnector?.dispose();
        this._aggregationRowConnector = undefined;
        this._aggregationRowAction.locKey = ListFrame._SHOW_AGGREGATION_ROW_LOC_KEY;
    }

    protected override async _initializeDataGrid(): Promise<void> {
        const initDataGridFnc = async () => {
            await this._createAndInitDataGrid();
            this.dataGrid.showFrameMenu = true;
            this.compactDataGrid.showFrameMenu = true;

            this._componentsElement.appendChild(this.dataGrid);
            this._componentsElement.appendChild(this.compactDataGrid);

            if (this.masterFrame && (this.masterFrame instanceof ListFrame)) {
                this.masterFrame.dataGrid.autoSelectFirstRecord = true;
            }

            this.dataGrid.interactive = this.interactive;
            this.compactDataGrid.interactive = this.interactive;

            this.dataGrid.cellValidityChanged.add(async (sender, data) => {
                this.enqueuePendingUpdateTask((async () => {
                    if (this.actualObject) {
                        const rowObj = this.actualObject.id == data.row.dataKey
                            ? this.actualObject
                            : await this._getObject(data.row.dataKey);
                        await this.waitForPendingUpdates();
                        this._cellValidityChanged(sender as IValidatable & IAttributeControl, rowObj, data.validity);
                    }
                })());
            });

            this.dataGrid.permissionsChanged.add((sender, data) => {
                this._rowPermissionsChanged(sender.dataKey, data);
            });

            if (this.disableSort)
                this.dataGrid.disableSort();

            if (this._groupColumns && !this.disableGrouping) { // Grupovací strom se vytváří až na konec, aby bylo možné ho provázat s gridem seznamu
                await this._createGroupTree(this._groupColumns);
            }

            // Odebrání/přidání akce pro agregační řádek z/do menu podle viditelnosti prázdné zprávy
            this.dataGrid.emptyMessage.addEventListener("visibility", (e: IBooleanDetailValueEvent) => {
                this._updateAggregationRowAction(e.detail.value);
            });

            this._isDataGridInitialized = true;
            this.dataGridInitialized.trigger(this, this.dataGrid);
        };

        try {
            if (this.canValueLoad || this.isMainFrame) { // grid se vytváří do viditelných
                await initDataGridFnc();
                if (this.dataGrid.columns.length > 0) {
                    const attributeNamesWithEncryptingAllowed = await this._getAttributeNamesWithEncryptingAllowed();
                    for (const column of this.dataGrid.columns) {
                        column.addEventListener("visibility", (event) => {
                            const visible = event.detail.value;
                            if (visible) {
                                const isColumnEncryptingAllowed = attributeNamesWithEncryptingAllowed.includes(column.attrName);
                                this.columnAdded.trigger(this, { column: AggregationRowConnector.columnToColumnDefinition(column, isColumnEncryptingAllowed) });
                                this.orderOfColumnsChanged.trigger(this, { orderOfColumns: this._getOrderOfColumns() });
                            } else {
                                this.columnRemoved.trigger(this, { columnKey: column.columnId });
                            }
                        });
                    }
                }
                this.dataGrid.addEventListener("ColumnPositionChanged", () => {
                    this.orderOfColumnsChanged.trigger(this, { orderOfColumns: this._getOrderOfColumns() });
                });
            }

            //let vTime: number = undefined;
            this.canValueLoadChanged.add(this._canValueLoadChangedListener = async (_s: Control, val: boolean) => {
                if (val) {
                    if (!this.dataGrid) {
                        await initDataGridFnc();
                    }
                    if (this._pendingMasterObjectChange)
                        await this._onMasterObjectChanged();
                    await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.initialized); // TODO-rites: ~nechat tady? asi jo; c2: spouštět až po inicializaci všech framů, jinak mohlo dojít k situaci, že se na LF iniciuje vyhodnocení práv a stromem projde na podřízený detail, který ještě není inicializován a chybí mu třeba rightsManager
                    await this.checkIfIsCompact();
                }
            });
        } catch (e) {
            ErrorHandler.instance.handleError(e);
        }
    }

    /**
     * Aktualizuje přítomnost akce pro zobrazení/skrytí patičky s agregačním řádkem v menu frame.
     * Pokud list nemá žádná data k zobrazení, pak není k dispozici ani akce agregačního řádku.
     * @param emptyMessageVisible Příznak, zda je viditelná zpráva o prázdném obsahu.
     */
    private _updateAggregationRowAction(emptyMessageVisible: boolean): void {
        if (!this._aggregationRowAction) {
            return;
        }

        const isAggregationRowActionInMenu = !!this.menu.getAction(ActionType.standardToggleAggregationRow);
        const actionShouldBeHidden = (emptyMessageVisible || this.compactDataGrid?.visible);

        if (actionShouldBeHidden && isAggregationRowActionInMenu) {
            this.menu.removeAction(this._aggregationRowAction);
        } else if (!actionShouldBeHidden && !isAggregationRowActionInMenu) {
            this.menu.addAction(this._aggregationRowAction);
        }

        this._triggerComputedChoiceCellWidthChanged();
    }

    /**
     * Získá pořadí sloupců včetně sloupce s ovládacími prvky (ten je vždy první).
     */
    private _getOrderOfColumns(): readonly string[] {
        return [ListFrame._CONTROL_COLUMN_ID, ...this.dataGrid.columns.filter((column) => column.visible).map((column) => column.columnId)];
    }

    protected override async _initializeFormUI(): Promise<void> {
        await super._initializeFormUI();

        if (this.menu && this._fulltextAttributes.length > 0)
            this.menu.setProperty(MenuUIExtensionProperties.fulltextSupported, true);

        this._registerLovButton();
    }

    protected override async _initializeActions(): Promise<void> {
        await super._initializeActions();

        FrameSharedMethods.addFulltextSearch(this);
        const downloadAction = FrameSharedMethods.createDownloadAction(this);
        if (downloadAction)
            this.menu.addAction(downloadAction);
    }

    /** Stará se o automatické načtení dat po inicializaci datagridu, pokud je seznam masterem */
    protected override _loadFrameData(): void {
        if (this.isMainFrame && this.ownerForm.openMode === OpenModeType.insert && this._permanentInvisible) {
            return;
        }

        if (this._shouldApplyQueryMode()) { // Zabrání zaregistrování listenerů, které by jinak načetly data po inicializaci
            return;
        }

        this._queryModeFinished = true; // Došlo k načtení dat
        this.initialized.add(async () => {
            this.dataGridInitialized.add(async () => {
                // Nastavení filtru musí proběhnout až po inicializaci dataGridu, protože až v té chvíli existuje FilterManager
                if (this.defaultFilterId) {
                    this.filterManager.selectFilterById(this.defaultFilterId);
                }
            });
        }, true);

        super._loadFrameData();
    }

    /**
     * Vytvoří groupovací strom
     * @param attributes Atributy ke zobrazení
     */
    private async _createGroupTree(attributes: Array<string>): Promise<void> {
        this._groupGrid = new GroupingTreeViewGrid();

        await this._groupGrid.headerRow.addTextElement(null, "GUI-664797"); // Seskupeno podle

        this._groupGrid.attributesToGroup = attributes;
        this._groupGrid.formattingColumns = this.dataGrid.columns;
        // Informace pro načtení dat jsou aktualizovány průběžně a proto nejsou nastaveny přes konstruktor. Defaultně nenačítáme žádná data přes "1=0"
        this._groupGrid.dataSource = new GroupingTreeViewDataSource({
            className: this.objectName,
            condition: "1=0",
            frameContext: this.frameContext,
            readVersioned: this.readVersioned,
            dataLength: this._distinctFullLength ? -1 : 0, //pokud je na frame zapnutá vlastnost distinctFullLength nezkracují se hodnoty v seznamu grup
        });
        this._groupGrid.setAttribute("style", `flex: 0 0 ${this._groupWidth}px`); // `

        this._groupGrid.headerRow.close.add(() => {
            if (this.dataSource.hasAdditionalCondition()) {
                this.dataSource.clearAdditionalConditions();

                this.refreshData(false);
            }
            this.currentAttributesToGroup = undefined;
            this._removeGroupTree();
            this.frameProfileChanged.trigger(this, null);
        });

        this._groupGrid.addEventListener("SelectionChanged", this._groupSelectionChangedListener = (e: IDataGridSelectionChangedEvent) => {
            this._groupTreeSelectionChanged(e);
        });

        attributes.forEach((attribute) => { // Lokalizuje seznam sloupců v hlavičce grupovacího stromu
            const attrColumn = this.dataGrid.columns.find((column) => StringUtils.compareCaseInsensitive(column.filterAttrName, attribute));
            this._groupGrid.headerRow.addTextElement(attrColumn.attrName, attrColumn.locKey, attrColumn.locParams);
        });

        this._componentsElement.insertBefore(this._groupGrid, this.dataGrid);
    }

    /**
     * Aktualizuje data načtená v seznamu podle právě vybraného záznamu ve filtrovacím stromu
     * @param e Událost, obsahující informce o označeném záznamu
     */
    private _groupTreeSelectionChanged(e: IDataGridSelectionChangedEvent): void {
        if (this.dataSource instanceof IDataSourceExtended) {
            this.dataSource.clearAdditionalConditions();

            if (e.detail.rowID != GenericListFrame._NO_ID) {
                const conditionData = (e.detail as IGroupingDataGridSelectionChangedEventData).conditionData;

                conditionData.forEach((item, index) => {
                    /*Pokud se jedná o řetězcovou zkrácenou hodnotu, použijeme operátor LIKE jinak se použivá standardní EQUAL.*/
                    const attrValue = e.detail.data[e.detail.data.attribute];
                    if (attrValue instanceof IAttrStringValue && attrValue.shortened)
                        this.dataSource.setAdditionalCondition(e.detail.rowID + index, `:Linguistic.LIKE(${item.condition}, '${item.param}%')`);
                    else
                        this.dataSource.setAdditionalCondition(e.detail.rowID + index, `${item.condition} = @${this.dataSource.conditionParams.length}`, [item.param]); // `
                });

                this.clearSelectedRows();
                this.refreshData(false);
            } else {
                this.refreshData(true);
            }
        } else {
            throw new Error("Unable to filter ListFrame. Unsupported DataSource type.");
        }
    }

    /** Aktualizuje zobrazené atributy v třídícím stromu */
    private async _updateGroupTreeAttributes(column: Column, callFrameProfileChangedEvent: boolean): Promise<void> {
        const attrName = column.filterAttrName;
        if (this._groupGrid) {
            this.dataSource.clearAdditionalConditions(); // O aktualizaci seznamu se postará this.refreshGroupTree, který označuje dříve vybrané záznamy a spouští i refresh seznamu

            if (this._groupGrid.attributesToGroup.includes(attrName)) {
                if (this._groupGrid.attributesToGroup.length === 1) { // Odstraňujeme poslední sloupec, zrušíme tedy celý strom
                    this.currentAttributesToGroup = undefined;
                    this._removeGroupTree();

                    await this.refreshData(false);
                    if (callFrameProfileChangedEvent)
                        this.frameProfileChanged.trigger(this, null);

                    return;
                } else {
                    this._groupGrid.attributesToGroup.splice(this._groupGrid.attributesToGroup.indexOf(attrName), 1);
                    this._groupGrid.headerRow.removeTextElement(attrName, column.locKey);
                }
            } else {
                this._groupGrid.attributesToGroup.push(attrName);
                this._groupGrid.headerRow.addTextElement(attrName, column.locKey, column.locParams);
            }

            if (this._canGroupTreeLoadData()) {
                await this.refreshGroupTree(true);
            }
        } else {
            await this._createGroupTree([attrName]);
            await this._loadGroupTreeData();
        }

        this.currentAttributesToGroup = this._groupGrid.attributesToGroup;
        if (callFrameProfileChangedEvent)
            this.frameProfileChanged.trigger(this, null);
    }

    /**
     * Aktualizuje podmínku a načte data do grupovacího stromu
     * @param initializeDataSourceOnly Zda má být pouze inicializovaný dataSource bez načtení dat
     */
    private async _loadGroupTreeData(initializeDataSourceOnly: boolean = false): Promise<void> {
        if (this._canGroupTreeLoadData()) {
            // Nechceme použít condition z DS, protože už obsahuje omezující podmínky ze stromu. Zároveň nechceme ani this.condition, protože neobsahuje omezující vazby
            await this._createConditionAndConditionParams(this._groupGrid.dataSource);
            this._groupGrid.dataSource.setFilterCondition(this._filterCondition);

            if (!initializeDataSourceOnly) {
                await this.refreshGroupTree();
            }
        }
    }

    /** Odstraní třídící strom ze seznamu */
    private _removeGroupTree(): void {
        this._groupGrid.removeEventListener("SelectionChanged", this._groupSelectionChangedListener);

        this._groupGrid.remove();
        this._groupGrid = null;
        this.currentAttributesToGroup = undefined;
    }

    /** Zda jsou splněny všechny podmínky nutné pro spuštění načítání do groupovacího stromu */
    private _canGroupTreeLoadData(): boolean {
        return (this.isMainFrame || this.isMainSelectionFrame || this.isMasterRecordSelected()) && !this._isInQueryMode();
    }

    /**
     * Aktualizuje data v grupovacím stromu, pokud existuje
     * @param keepSelectedRecords Zda má zůstat vybraný záznam a navazující filtr
     */
    public async refreshGroupTree(keepSelectedRecords: boolean = false): Promise<void> {
        if (this._groupGrid && !this._isDisposed) {
            // Je třeba přejít z QueryMode do normálního režimu
            if (this._isInQueryMode()) {
                await this.refreshData(false);
            }

            await this._groupGrid.refreshData(keepSelectedRecords);
        }
    }

    /** Vytváří menu v hlavičce seznamu */
    protected override _createDesktopDropDownMenu(): void {
        super._createDesktopDropDownMenu();

        if (!this.disableGrouping) {
            const groupButton = DropDownMenuItem.createDropMenuItem("none", async () => this._updateGroupTreeAttributes(this._headerDropDownHc, true), "GUI-666295", true); // Seskupit podle tohoto sloupce
            groupButton.classList.add("menu-grouping-item");

            this._headerDropDown.addItem(groupButton);

            this._headerDropDown.addEventListener("open", () => {
                if (this._groupGrid && this._groupGrid.attributesToGroup.includes(this._headerDropDownHc.attrName)) {
                    groupButton.icon = "checked";
                } else {
                    groupButton.icon = "none";
                }
            });
        }
    }

    private _registerLovButton(): void {
        //tlacitko vybrat a dalsi funkcionalita na lov
        if (this.isLovFrame) {
            //Vybrat
            const selectItemAction = FrameSharedMethods.createStandardRecordAction(ActionType.standardSelectRecord, (e: Event, dataKey: string) => {
                if (e)
                    EventHelper.stopPropagation(e);
                //this.selectLovValue(dataKey);
                return this.selectLovValue(dataKey);
            }, this);

            this.menu.addAction(selectItemAction);

            selectItemAction.enabled = true;
            //Pokud by akce FormUI renderovalo jako toolbar button, je potřeba nastavit do property action.
            selectItemAction.action = () => selectItemAction.recordAction(null, this.actualObject.id);
        }
    }

    protected override async _addStandartActions(isMainFrame: boolean): Promise<void> {
        await super._addStandartActions(isMainFrame);
        let action: IAction;

        if (!this._hideFilterRow) {
            this._filterAction = FrameSharedMethods.createFilterAction(ActionType.standardFilter, async () => {
                if (this.ownerForm.filterPanel.isShown(this.frameName)) {
                    this.ownerForm.filterPanel.hide();
                } else {
                    this._showFilterPanel();
                }
            }, this);
            this.menu.addAction(this._filterAction);

            this._filterListAction = FrameSharedMethods.createFilterAction(ActionType.standardFilterList, async () => {
                if (!this.interactive) {
                    return;
                }
                if (this._filterMenu && this._filterMenu.isOpened) { // pri prvnim pokusu o otevreni jeste filterMenu neexistuje
                    this._filterMenu.close();
                } else {
                    await this._createFilterBtnMenu(); // pri zavreni se odregistruji udalosti, proto vytvarim znova
                    this._filterMenu.show(this._filterListAction.toolbarButton.element, null);
                }
            }, this);
            this.menu.addAction(this._filterListAction);
            // Pokud je profilem nastaven filtr, který se má po inicializaci načíst,
            // nastavíme filtrovacímu tlačítku jeho název
            if (this.defaultFilterId && this.filterManager?.currentFilter) {
                this._setFilterToolbarCaption(this.filterManager.currentFilter.filterName);
            }
        }

        if ((action = FrameSharedMethods.createMultiSelectAction(ActionType.standardDeselectAll, async () => {
            if (!(await DataGridSharedMethods.canChangeSelectedRow(this.dataGrid, true)))
                return;

            this.isEditing = false;
            this.isChanged = false;

            this.clearSelectedRows();
            this.setToolbarToMultiselectMode(false);
            FrameSharedMethods.clearWorkflowItems(this._wfMultiActions, this.menu); // odstraní WF akce z toolbaru
        }, this)) != null) {
            this.menu.addAction(action);
        }

        if ((action = FrameSharedMethods.createMultiSelectAction(ActionType.standardSelectAll, async () => {
            if (!(await DataGridSharedMethods.canChangeSelectedRow(this.dataGrid, true)))
                return;
            this.currentDataGrid.selectAllRows();
        }, this)) != null) {
            this.menu.addAction(action);
        }

        if (this.detailForm && !this.detail && this.supportsMultiEditing) {
            this._createEditAction(false);
        }
    }

    protected override async _registerRecordActions(): Promise<void> {
        await super._registerRecordActions();

        const cancelAction = FrameSharedMethods.createStandardRecordAction(ActionType.standardCancelRecordChanges, (e: Event, dataKey: string) => {
            if (e)
                EventHelper.stopPropagation(e); // zrušní kliknutí, aby se neoznačil řádek, na kterém se ruší změny
            return this._cancelRowChanges(dataKey);
        }, this);

        this.menu.addAction(cancelAction);

        if (!this._showOnMainForm && this.detailForm) {
            const detailInWindowAction = FrameSharedMethods.createStandardAction(ActionType.standardOpenDetailOnMainForm, async () => {
                this.showSaveChangesDialog(async () => {
                    return await this._openDetailSeparately({
                        openInEdit: this._openMode === OpenModeType.edit,
                        showOnMainForm: true,
                    });
                });
            }, this);

            detailInWindowAction.toMenu = MenuType.mtInfo;
            detailInWindowAction.enabled = true;

            this.menu.addAction(detailInWindowAction);
        }
    }

    /** Zobrazí filtrovací panel */
    private _showFilterPanel(): void {
        this._filterManager.updateFilterPanel();

        this.ownerForm.filterPanel.color = this.headerColor;
        this.ownerForm.filterPanel.show();
    }

    /**
     * Nastaví popisek filtru na tlačítko filtrů
     * @param caption Požadovaný popisek
     */
    private _setFilterToolbarCaption(caption: string = null): void {
        if (this._filterListAction && this._filterListAction.toolbarButton)
            this._filterListAction.toolbarButton.setCaption(caption);
    }

    /** Přepne datagridy do režimu QueryMode */
    public setToQueryMode(dataLoad: () => Promise<any>): void {
        this.dataGrid.setToQueryMode(dataLoad);
        this.compactDataGrid.setToQueryMode(dataLoad);

        if (!this._hideFilterRow) {
            this._showFilterPanel();
        }

        if (this.dataGrid instanceof EditableDataGrid) {
            this.dataGrid.registerLoadDataQueryMode(dataLoad);
        }
    }

    /**
     * Přidá predikát pro vyhodnocení editovatelnosti
     */
    public addCanEditPredicate(predicate: (...params: any[]) => boolean): void {
        this._canEditPredicates.push(predicate);
    }

    /**
     * Oveří existenci predikátu pro vyhodnocení editovatelnosti
     */
    public containsCanEditPredicate(predicate: (...params: any[]) => boolean): boolean {
        return this._canEditPredicates.indexOf(predicate) > -1;
    }

    /**
     * Odstraní predikát pro vyhodnocení editovatelnosti
     */
    public removeCanEditPredicate(predicate: (...params: any[]) => boolean): boolean {
        const indexOfPredicate = this._canEditPredicates.indexOf(predicate);
        if (indexOfPredicate > -1) {
            this._canEditPredicates.splice(indexOfPredicate, 1);
            return true;
        }
        return false;
    }

    /**
     * Pokud muze tak do toolbaru prida editaction
     * @param hbEdit - hodnoto z hidebuttons
     */
    protected _createEditAction(hbEdit: boolean): void {
        if (hbEdit)
            return;

        if (this.supportsMultiEditing) {
            const action = FrameSharedMethods.createMultiSelectAction(ActionType.standardEditRecords, async () => {
                await this._openDetailSeparately();
            }, this);

            if (this.actualObjectStandardRights.canEdit) {
                action.enabled = true;
            }
            this.menu.addAction(action);
        }
    }

    /**
     * /
     * @param objectId - vyplneny jen v pripade ze kliknu na tlacitko vybrat na radku. V tom pripade totiz nedojde k vyberu radku a je potreba predat primo ID zaznamu, ktery se vlozi do LOV
     */
    public async selectLovValue(objectId?: string): Promise<void> {
        await PromiseUtils.delay(100);

        //v seznamu je zmeneny objekt, proto jej musim nejdriv vyresit jeho ulozeni, nez provedu vyber na LOV
        if (this.currentDataGrid instanceof IEditableListDataGrid && this.dataGrid.editedRows.length > 0) {
            const r = await MessageWindow.showYesNoCancelDialog(new LocalizedStringBuilder("GUI-584552"), "GUI-6", "GUI-605905", "GUI-33412"); //"GUI-584552"=Před vybráním záznamu musíme uložit všechny změny. Přejete si uložit provedené změny?,"GUI-6"=Uložit,"GUI-605905"=Neukládat,"GUI-33412"=Zrušit
            switch (r.result) {
                case MessageButtonType.ok:
                    if (await this.saveRecords() === null)
                        this._finishSettingLOVValue();
                    break;
                case MessageButtonType.no:
                    try {
                        await this.cancelChanges();
                        this._finishSettingLOVValue(objectId);
                    } catch (e) {
                        logger.error(e);
                    }
                    break;
                case MessageButtonType.cancel:
                    return;
            }
        } else
            this._finishSettingLOVValue(objectId);
    }

    private async _finishSettingLOVValue(objectId?: string): Promise<void> {
        const recordIdentification: IRecordIdentification = {
            className: this.objectName,
            id: objectId ?? this.actualObject?.id,
        };

        this.ownerForm.close(new FormResult(recordIdentification));
    }

    /**
     * Handler události kliknutí na hlavičku seznamu - vyvolává řazení a nezbytné kontroly
     * @param col Sloupec, na který se handler zavěsí
     */
    public override registerHeaderClickEvent(col: Column): void {
        col.registerEventListener("HeaderClickEvent", async (e: CustomEvent) => {
            if (!this.interactive || !col.canSort)
                return;
            if (this.isEditing && this.dataGrid.editedRows.length === 0)
                await PromiseUtils.delay(300);

            await this.waitForPendingUpdates();

            this.showSaveChangesDialog(async () => {
                this.setToolbarToMultiselectMode(false); //TODO: Odstranit až se udělá rozvoj pro práci s více záznamy [TP]
                const objectClass = await this._getClassDescription();
                const attrType = (await objectClass.getAttributeInfo(col.attrName)).basicType;

                if (CompactDataGrid.isCompactGridShow(this.element) && this._gridStyle == ListFrameGridStyle.gsauto || this._gridStyle == ListFrameGridStyle.gscompact) {
                    this.compactDataGrid.changeOrdering(col, e.detail.state === 1 ? OrderType.ascending : e.detail.state === 2 ? OrderType.descending : null, e.detail.shiftKey,
                        attrType == BasicDataType.String || attrType == BasicDataType.StrNum ? AdditionalOrdering.linguistic : null);
                } else {
                    this.dataGrid.changeOrdering(col, e.detail.state === 1 ? OrderType.ascending : e.detail.state === 2 ? OrderType.descending : null, e.detail.shiftKey,
                        attrType == BasicDataType.String || attrType == BasicDataType.StrNum ? AdditionalOrdering.linguistic : null);
                }

                this.frameProfileChanged.trigger(this, null);
            });
        });
    }

    /**
     * Vyvolava YNC dialog a pokud neni jeho vystupem storno (cancel), tak setridi seznam.
     * @param orderType typ trideni
     */
    protected async _orderBy(order: IOrderBy[]): Promise<void> {
        await this.waitForPendingUpdates();
        this.showSaveChangesDialog(async () => {
            if (this.currentDataGrid instanceof IListDataGrid)
                this.currentDataGrid.setOrderBy(order);
        });
    }

    private _prepareDataGridToChangeView(oldDataGrid: IDataGrid<IDataRowElement>, newDataGrid: IDataGrid<IDataRowElement>): string[] {
        newDataGrid.clearSelectedRows();

        const selectedRows: string[] = [];
        oldDataGrid.selectedRows.forEach(row => selectedRows.push(row.dataKey));

        return selectedRows;
    }

    /**
     * Kontrola zda je nebo neni zobrazeno kompaktni zobrazeni
     */
    public override async checkIfIsCompact(): Promise<void> {
        // ve FF ještě nemusí být vypočítaná this.element.offsetWidth, takže se předá řízení prohlížeči,
        // aby si to nejdřív dopočítal (možná problém v kombinaci se styly nastavují šířku pomocí calc())
        await PromiseUtils.delay(0);

        if (this.element.clientHeight == 0 && this.element.clientWidth == 0) // nema smysl pokud je aktualni velikost nulova
            return;
        if (!this.compactDataGrid)
            return;

        // mezitím se zavřel formulář
        if (this._isDisposed)
            return;

        if (CompactDataGrid.isCompactGridShow(this.element) && this._gridStyle == ListFrameGridStyle.gsauto || this._gridStyle == ListFrameGridStyle.gscompact) {
            if (this.dataGrid.visible) {
                this._setCurrentDataGrid(this.compactDataGrid);

                const selectedRows = this._prepareDataGridToChangeView(this.dataGrid, this.compactDataGrid);
                this.compactDataGrid.resizeGrid();
                if (this.compactDataGrid.isShowed)
                    return;
                this.dataGrid.visible = false;
                this.compactDataGrid.visible = true;

                this.compactDataGrid.isShowed = true;
                this.compactDataGrid.orderBy = this.dataGrid.orderBy;
                await this.dataSource.waitForReload();
                await this.compactDataGrid.reRenderData();

                if (this.compactDataGrid.dataRows.length == 0) {
                    if (!this.compactDataGrid.initialized) {
                        this.compactDataGrid.registerEventListener("Initialized", () => {
                            this.dataSource.getNextData(this.compactDataGrid.startIndex, this.compactDataGrid.fetchCount);
                        });
                    }
                }

                if (selectedRows.length > 0)
                    selectedRows.forEach(dataKey => this.compactDataGrid.selectRow(dataKey));

                this._updateAggregationRowAction(false /*nezáleží - pro kompakt se vždy skryje*/);
            }
        } else {
            if (this.compactDataGrid.visible) {
                const selectedRows = this._prepareDataGridToChangeView(this.compactDataGrid, this.dataGrid);

                this.compactDataGrid.visible = false;
                this.dataGrid.visible = true;
                this.dataGrid.orderBy = this.compactDataGrid.orderBy;
                this.dataGrid.resizeGrid();
                await this.dataGrid.reRenderData();
                this._setCurrentDataGrid(this.dataGrid);

                if (!this.compactDataGrid.isShowed)
                    return;
                this.compactDataGrid.isShowed = false;

                if (selectedRows.length > 0)
                    selectedRows.forEach(dataKey => this.dataGrid.selectRow(dataKey));

                this._updateAggregationRowAction(this.dataGrid?.emptyMessage?.visible);
            }
        }
    }

    protected override async _createAndInitDataGrid(): Promise<void> {
        this.compactDataGrid = new CompactDataGrid();

        if (this.compactDataGrid.emptyMessage) {
            this.compactDataGrid.emptyMessage.emptyMessageCustomization = this.emptyMessageCustomization;
        }

        this.dataGridCreated.trigger(this, this.compactDataGrid);

        this.compactDataGrid.dataSource = this.dataSource;
        this.compactDataGrid.orderBy = this.orderBy;
        this.compactDataGrid.visible = false;
        this.compactDataGrid.setExtendedProperties(this._getExtendedProperties());

        this._events.addListener(this.compactDataGrid, "AfterSaved", () => {
            this.autoRefresher?.unpause();
        });

        await super._createAndInitDataGrid();

        if (this.isMainFrame) {
            if (this.ownerForm.autoSelectFirstRow != null && this.ownerForm.selectedId == null) {
                this.dataGrid.autoSelectFirstRecord = this.ownerForm.autoSelectFirstRow;
                this.compactDataGrid.autoSelectFirstRecord = this.ownerForm.autoSelectFirstRow;

                if (this.ownerForm.autoSelectOnlyIfOneRow != null) {
                    this.dataGrid.autoSelectOnlyIfOneRow = this.ownerForm.autoSelectOnlyIfOneRow;
                    this.compactDataGrid.autoSelectOnlyIfOneRow = this.ownerForm.autoSelectOnlyIfOneRow;
                }
                if (this.ownerForm.openInEdit) {
                    this.dataGrid.autoSelectOnlyIfOneRow = false;
                    this.compactDataGrid.autoSelectOnlyIfOneRow = false;
                }
            } else {
                this.dataGrid.autoSelectFirstRecord = true;
                this.compactDataGrid.autoSelectFirstRecord = true;
            }
        }

        //if (this.OwnerForm.HasDetailFrame() && !this.MasterFrame) {
        //    this.OwnerForm.DetailFrame.DetailResized.Add(() => this.checkIfIsCompact());
        //}
        let resizeDetailChecked: number = undefined;
        window.addEventListener("resize", this._eventFunction["resize"] = () => {
            if (!resizeDetailChecked)
                clearTimeout(resizeDetailChecked);
            resizeDetailChecked = self.setTimeout(() => this.checkIfIsCompact(), 150);
        });
        window.addEventListener("resizecomponent", this._eventFunction["resizecomponent"] = () => {
            if (!resizeDetailChecked)
                clearTimeout(resizeDetailChecked);
            resizeDetailChecked = self.setTimeout(() => this.checkIfIsCompact(), 150);
        });

        this.dataGrid.addEventListener("resizablestop", (e) => {
            const ht = e.target as HeaderTemplate;
            const width = ht.parentColumn.width;
            this._setProfileColumnInfoAttr(ht.parentColumn.attrName, FrameColumnInfoAttributes.width, width);
            this.frameProfileChanged.trigger(this, null);
            this.columnWidthChanged.trigger(this, {
                columnKey: ht.parentColumn.columnId,
                width,
            });
        });

        this.compactDataGrid.addEventListener("DataShown", async () => {
            this._dataShown();
        });

        const selectionChangedPreviewHandler = (e: IAwaitableEvent<boolean>) => {
            const detail: SelectionChangedPreviewEventArgs = e.detail;
            // k možným existujícím taskům z jiných handlerů přidám další - výsledkem celého chainu může být zrušení cílové události (při false)
            e.enqueueResult(async (res) => {
                // pokud už někde dříve bylo výsledkem false, tak nemá cenu provádět další vyhodnocení
                if (res === false)
                    return false;

                // počkám na možné probíhající updaty a reloady
                await this.dataSource.waitForReload();

                const selectAll = !!detail?.selectAll;

                // Pokud nejde o výběr všech, není aktivní multieditace, je esSingle a záznam je změněný, tak se zobrazuje dotaz na uložení
                // a jeho výsledek se vrací - pokud uživatel kliknul na storno, tak se ruší změna vybraného řádku
                if (!selectAll && !this.isMultiEditing && this.editStyle === EditStyleType.esSingle && this.isEditing && this.isChanged)
                    return this.ownerForm.checkAndSaveForm({ sourceFrame: null, forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext });
                // pokud ma seznam detail, je potreba zkontrolovat jestli na nem neni rozedtovany zaznam na seznamu
                if (!selectAll && !this.isMultiEditing && this.editStyle === EditStyleType.esSingle && this.ownerForm.detailFrame != null) {
                    let detailMaster = this.ownerForm.detailFrame.masterFrame;
                    if (detailMaster instanceof GenericListFrame)
                        detailMaster = !(detailMaster.editStyle === EditStyleType.esMulti) ? null : detailMaster;
                    //Jako source frame musi byt masterDetailu ne master aktualniho framu a musi se vzit v potaz jeho editStyle
                    if (this.isChanged && !this.masterFrame || this.detail)
                        return this.ownerForm.checkAndSaveForm({ sourceFrame: detailMaster, forceSave: this._forceAutoSave });
                }
                if (this.isMultiEditing) {
                    const valid = await this.validate(true, true);
                    if (!valid)
                        return false;
                    //zobrazí YNC a zeptá se jestli má pokračovat přes nevyplněné doporučené pole
                    const continueRecommended = await SharedMethods.checkObjectBeforeExecute(this.validationManager, "GUI-626119", "GUI-33412"); //Pokračovat;Zrušit
                    return continueRecommended !== ObjectCheckingResult.recommendedStop;
                }

                return true;
            });
        };

        // Preview událost umožňuje zrušit cílovou událost (SelectionChanged), ale neměla by sloužit k ničemu jinému
        this.dataGrid.registerEventListener("SelectionChangedPreview", selectionChangedPreviewHandler);
        this.compactDataGrid.registerEventListener("SelectionChangedPreview", selectionChangedPreviewHandler);

        const doubleClickHandler = async (e: CustomEvent) => {
            const detail: IDoubleClickEventArgs = e.detail;
            if (this.isLovFrame && this.ownerForm.enableLovSelect && detail.selectionByDoubleClick) {
                this.selectLovValue(e.detail.rowID);
                return;
            }
            if (this.isEditable || (!this.isEditable && this.detailForm)) {
                if (this.actualObject || this.onEditMethod || detail.selectionByDoubleClick) {
                    if (!this.actualObject || detail.rowID.toString() !== this.actualObject.id.toString()) {
                        try {
                            await EventAwaiter.createEventAwaiter(this.actualObjectChanged, 10000);
                        } catch (e) {
                            // do nothing.
                        }

                        await this.waitForActualObjectSet();
                        await this.waitForDataLoad();
                        if (!this.actualObject || detail.rowID.toString() !== this.actualObject.id.toString()) {
                            throw new InvalidOperationException("[ListFrame] Unable to load object for editing!");
                        }
                    } else if (this.isEditing) { // Double click na již vybraný a rozeditovaný záznam -> nechceme dělat nic
                        return;
                    }
                }
                this.editRecord();
            }
        };

        this.dataGrid.registerEventListener("DoubleClick", doubleClickHandler);
        this.compactDataGrid.registerEventListener("DoubleClick", doubleClickHandler);

        this.compactDataGrid.registerEventListener("SelectionChanged", (e: IDataGridSelectionChangedEvent) => {
            this.onSelectionChanged(e);
        });

        this.compactDataGrid.addEventListener("KeyboardSelectionChangeInitiated", () => {
            this.keyboardSelectionChangeInitiated.trigger(this, this.compactDataGrid);
        });

        if (this.ownerForm.detailFrame != null)
            this.ownerForm.detailFrame.detailResized.add(() => {
                this.checkIfIsCompact();
                this.currentDataGrid.resizeGrid();
            });

        this._orderByInitiated.add((_sender, order) => {
            this._orderBy(order);
        });

        this.validationManager.objectValidityChange.add((_s, state) => {
            for (const row of this.currentDataGrid.dataRows) {
                if (row.dataKey == state.object.id) {
                    row.isValid = state.validityState;
                    break;
                }
            }
        });

        this.ownerForm.stateChanged.add((_sender, value) => {
            if ((value.state == WindowState.active) && value.value) {
                this.checkIfIsCompact();
            }
        });

        await this._loadFilters();

        if (this._shouldApplyQueryMode()) {
            if (this.defaultFilterId) {
                this.filterManager.selectFilterById(this.defaultFilterId);
            }
            this.setToQueryMode(() => this._queryModeDataLoadMethod()); // Může být voláno až po this._loadFilters
        }

        await this.checkIfIsCompact();
    }

    /** Je spuštěna po dokončení načítání dat */
    protected override async _dataShown(): Promise<void> {
        await super._dataShown();

        if (this.ownerForm.selectedId) {
            const selectedId = this.ownerForm.selectedId;
            this.ownerForm.selectedId = undefined;

            await this.loadAndSelectRecord(selectedId);
        }

        if (this._showAggregationRow) {
            await this.showAggregationFooter();
            this._showAggregationRow = false; // použije se pouze pro první zobrazení
        }
    }

    private async _createFilterBtnMenu(): Promise<void> {
        const insertBtn = DropDownMenuItem.createDropMenuItem("insert",
            async () => {
                if (!this.ownerForm.filterPanel.isShown()) {
                    this._showFilterPanel();
                }
                this._filterManager.insertFilter();
            }, "GUI-674376", true); // Vložit filtr

        const advancedFilterBtn = DropDownMenuItem.createDropMenuItem("filter", async () => {
            this._showFilterPanel();
            this._filterManager.showAdvancedFilter();
        }, "GUI-691373", true); // Pokročilé filtrování

        const emptyBtn = DropDownMenuItem.createDropMenuItem("checked",
            async () => {
                this.showSaveChangesDialog(async () => {
                    this._filterManager.clearFilter();
                    emptyBtn.icon = "checked";
                    this._setFilterToolbarCaption();
                });
            }, "GUI-670216", true); // Vše
        if (this._filterClearedListener) {
            this._filterManager.filterCleared.remove(this._filterClearedListener);
        }

        this._filterManager.filterCleared.add(this._filterClearedListener = () => {
            emptyBtn.icon = "checked";
        }, false, true);

        if (this._filterChangedListener) {
            this._filterManager.filterChanged.remove(this._filterChangedListener);
        }

        this._filterManager.filterChanged.add(this._filterChangedListener = (_filterManager, _filterBlock) => {
            emptyBtn.icon = "none";
        });

        const filterMap = await this._filterManager.getFilterList();

        // vytvořené menu se nesmí smazat, jinak se uživateli do otevřeného menu nepromítnou nové položky
        if (this._filterMenu == null) {
            this._filterMenu = new DropDownMenu();
            this._filterMenu.horizontalOpenPosition = DropDownMenuHorizontalOpen.right;
        } else {
            this._filterMenu.clearItems();
        }

        this._filterMenu.addItem(insertBtn); // Vložit
        this._filterMenu.addItem(advancedFilterBtn); // Pokročilé filtrování
        this._filterMenu.addItem(new SeparatorMenuItem());
        this._filterMenu.addItem(emptyBtn); // Žádný

        const dropDownItemsPromiseArr: Promise<DropDownMenuItem>[] = [];

        for (const filterInfo of filterMap) {
            dropDownItemsPromiseArr.push(this._filterManager.createFilterMenuItem(filterInfo[1], emptyBtn));
        }

        const resolvedItemArray = await Promise.all(dropDownItemsPromiseArr);

        for (const dropDownItem of resolvedItemArray) {
            this._filterMenu.addItem(dropDownItem);
        }
    }

    private async _executeFilter(filterData: IFilterExecutedEventArgs): Promise<void> {
        if (!(await this.ownerForm.checkAndSaveForm({ mostTopFrame: this, cancelEditIfNotEdited: true, forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext }))) {
            return;
        }

        this._queryModeFinished = true;
        this._filterManager.updateFilterPanel();

        this.ownerForm.filterPanel.color = this.headerColor;
        if (!this._showFilterValues && !this.isInitExecuteFilter) {
            this.ownerForm.filterPanel.show();
        }

        this.dataSource.setFilterCondition(filterData.condition);
        this._filterCondition = filterData.condition;

        if (this._showFilterValues) {
            this.dataGrid.setFilters(filterData.filter);
        }

        if (this._groupGrid) {
            this.dataSource.clearAdditionalConditions();
            this._groupGrid.dataSource.setFilterCondition(filterData.condition);

            await this._loadGroupTreeData();
        }

        this.dataGrid.clearSelectedRows();

        // Loading dialog spuštěn na GenericListFrame
        if (this.masterFrame != null && this.masterObject != null || this.masterFrame == null) {
            this.dataSource.loadNewData(true, this._cancelPrevAndCreateDataLoadingCTS().token).then(() => {
                let filterActive = false;
                for (const col of this.dataGrid.columns) {
                    if (filterData.columns && filterData.columns.get(col.attrNames[0])) {
                        col.isFiltered = true;
                        filterActive = true;
                    } else
                        col.isFiltered = false;
                }

                if (!this._hideFilterRow) {
                    if (filterActive || this._filterManager.hasActiveAdvancedFilter) {
                        this._filterAction.toolbarButton.setIcon(Icon.filterFilled);
                    } else {
                        this._filterAction.toolbarButton.setIcon(Icon.filter);
                    }
                }

                this.element.classList.toggle("filter-on", filterActive);
            }).catch(PromiseUtils.cancellationCatch);

            this.refreshGroupTree();
            this.filterExecuted.trigger(this, undefined);
        }

        this.isInitExecuteFilter = false;
    }

    private _clearFilter(condition: string): void {
        this._filterAction.toolbarButton.setIcon(Icon.filter);
        this._setFilterToolbarCaption();

        if (this._showFilterValues) {
            this.dataGrid.setFilters(null);
        }

        this.dataSource.setFilterCondition(condition);
        this._filterCondition = condition;

        if (this._groupGrid) {
            this._groupGrid.dataSource.setFilterCondition(condition);
        }

        this.dataGrid.clearSelectedRows();

        if (!this._isInQueryMode() && (this.masterFrame != null && this.masterObject != null || this.masterFrame == null)) {
            this.dataSource.loadNewData(true, this._cancelPrevAndCreateDataLoadingCTS().token);

            this.dataGrid.columns.forEach((column) => column.isFiltered = false);

            this.refreshGroupTree();

            this.filterExecuted.trigger(this, undefined);
        }
    }

    /**
     * Načte filtry pro ListFrame
     */
    private async _loadFilters(): Promise<void> {
        const columns = await FilterManager.getColumnListByDataGridColumns(this.dataGrid.columns, this.objectName);
        this._filterManager = new FilterManager({
            filterPanel: this.ownerForm.filterPanel,
            columns,
            formName: this.ownerForm.formName,
            frameUniqueIdentifier: this.frameName,
            className: this.objectName,
            frameCondition: await this.createReaderCondition(),
            frameConditionParams: await this._createReaderConditionParams(),
            disableValueShortening: this._distinctFullLength, //Pokud je na frame zapnutá vlastnost distinctFullLength nezkracují se hodnoty v seznamu hodnot. Použito u filtru Hodnoty jsou/nejsou.
            currentUserId: AppContext.instance.currentUser?.id,
            frameContext: this.frameContext,
            canEditImplementationFilters: await DIContainer.instance.create(ISystemOperationRights).get(SystemOperation.EDITIMPPROFILE),
        });

        await this._filterManager.init();

        this._filterManager.filterChanged.add(async (_fm, fb) => {
            if (this._headerDropDownFilterItem && fb.bindingFilterBlock)
                this._headerDropDownFilterItem.filterBlock = fb.bindingFilterBlock;
            this._setFilterToolbarCaption(await this._filterManager.getFilterDisplayName(fb));
        });

        this._filterManager.filterExecuted.add(async (_s, filterEventArgs) => {
            this._executeFilter(filterEventArgs);
        });

        this._filterManager.filterSaveStateChanged.add(async (_sender, block) => {
            if (this._filterManager.getCurrentFilter()?.filterId === block.filterId)
                this._setFilterToolbarCaption(await this._filterManager.getFilterCaption(block));
        }, false, true);

        this._filterManager.filterCleared.add((_s, v) => {
            if (!this._hideFilterRow) {
                this._clearFilter(v);
            }
        });
    }

    /**
     * Nacteni OW objectu
     */
    public override async getActualObject(objectId: any, forceLoad = false, _objectName: string = this.objectName, ct?: CancellationToken): Promise<OWObject | null> {
        try {
            UIManager.instance.showLoader();

            return await this._getObject(objectId, forceLoad, ct);
        } catch (err) {
            if (err instanceof CancelError)
                throw err; // zrušení je potřeba propagovat, aby na něj mohl volající reagovat

            if (err instanceof EOWRecordPermissionException) {
                this._setActualObject(null);
                return null; // nezobrazujeme chybovou hlášku
            }

            ErrorHandler.instance.handleError(err);
            return null;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    public override async onSelectionChanged(evt: IDataGridSelectionChangedEvent): Promise<void> {
        return this._waitForSelectionChangedTask = this._waitForSelectionChangedTask.then(() => this._onSelectionChanged(evt)); // Při vyvolání změny položky LF se handler řetězí, viz komentář k property _waitForSelectionChangedTask
    }

    private async _onSelectionChanged(evt: IDataGridSelectionChangedEvent): Promise<void> {
        // pokud je označeno více záznamů, zjistí se, jestli pro ně existují WF akce
        await this._reloadMultiWFItems();

        if (this.currentDataGrid.selectedRows.length > 1 && this.actualObject?.isChanged) {
            this.dataSource.updateSelectedGroup(false, this.actualObject.id, [this.actualObject.id]);
        }

        const lastClickedRow = this.currentDataGrid.lastClickedRow;
        if (
            lastClickedRow &&
            !lastClickedRow.contains(document.activeElement) &&
            (!(document.activeElement instanceof IDataRowElement) ||
                (document.activeElement instanceof IDataRowElement &&
                    document.activeElement.dataGrid === this.currentDataGrid))
        ) {
            // Focus only when the focus is outside the row or on the row of the same grid
            lastClickedRow.focus();
        }
        // lastClickedRow může být disposed, pokud došlo ke změně výběru třeba filtrováním.
        this.dataSource.updateSelectedGroup(
            this.currentDataGrid.isSelectedAll,
            lastClickedRow?.isDisposed() ? undefined : lastClickedRow?.dataKey,
            this.currentDataGrid.selectedRows?.map(i => i.dataKey),
        );

        if (!this.isMultiSelecting && this._lastSelectedRecordId === evt.detail.rowID && this._lastSelectedRecordId !== GenericListFrame._NO_ID) {
            this.setToolbarToMultiselectMode(this.currentDataGrid.selectedRows.length > 1);
            return;
        }

        //logger.log("ListFrame: OnSelectionChanged", this.FrameName, evt.detail.rowID);
        await super.onSelectionChanged(evt);
        const withoutObject = () => {
            if (!this.isEditing)
                this.editModeChanged.trigger(this, false);

            if (this.isMultiSelecting) {
                //pokud je označeno více záznamů, vrací se místo OWObject zástupný objekt, ten nemá vlastní ID, protože reprezentuje vícero objektů
                this.dataSource.getObject(MULTI_OBJECT_ID, null);
            } else {
                this._setActualObject(null);
            }
            if (this.ownerForm.detailFrame && this.ownerForm.detailFrame.element) {
                this.ownerForm.detailFrame.element.classList.remove("isSelectedObject");
            }
        };

        const ct = this._actualObjectCTS ? this._actualObjectCTS.token : null;
        let isNewRowFirstSelect: boolean = false;

        if (evt.detail && evt.detail.data && evt.detail.data.newRowFirstSelect) {
            isNewRowFirstSelect = true;
        }

        // je potřeba si zapamatovat, jestli se loader dává na this.menu nebo this.detail.menu, aby se ze stejného místa odebral
        const showLoaderOnDetail = this.detail && this.detail.menu;
        if (!this.isMultiSelecting && evt.detail.rowID == GenericListFrame._NO_ID) {
            withoutObject();
        } else {
            try {
                if (!isNewRowFirstSelect) {
                    if (showLoaderOnDetail) {
                        this.detail.menu.showMenuLoader();
                    } else {
                        this.menu.showMenuLoader();
                    }
                }

                //pokud se jedná o editovatelný řádek, který je změněn, nesmí se realodnout.
                const row = this.currentDataGrid.dataRows.find(r => r.dataKey == evt.detail.rowID);
                const forceReload = !(row instanceof IEditableDataRowElement && row.isChanged);

                const actObj = await this.getActualObject(evt.detail.rowID, forceReload, this.objectName, ct);
                const selRow = this.currentDataGrid.selectedRows[0];

                if (actObj == null) {
                    if (selRow) selRow.isDeleted = true;
                    this.currentDataGrid.deselectRow(evt.detail.rowID);
                    withoutObject();
                    return;
                }

                if (this.isEditing) {
                    let isRowInEdit = true;
                    if (selRow instanceof IEditableDataRowElement) {
                        if (actObj.isClientSideEncrypted) {
                            selRow.isInEdit = false;
                            isRowInEdit = false;
                            if (this.editStyle === EditStyleType.esSingle)
                                this.isEditing = false; // vypne ListFrame z editace
                        } else
                            selRow.isInEdit = true;
                    }
                    if (!isRowInEdit && this.detail) {
                        await this.detail.setDetailToEditMode(false, false);
                        this.detail.forceInEditStandartActionsState = true;
                    } else {
                        this.editModeChanged.trigger(this, isRowInEdit);
                    }
                } else {
                    if (this.detail)
                        this.detail.forceInEditStandartActionsState = undefined;
                }

                // Nějaká taková ochrana, kdyby náhodou od někud došlo (opožděně) k volání setu objektu, jehož id se liší od id vybraného řádku
                // Dochazí k přetypování stringu a čísla, proto !=
                // eslint-disable-next-line eqeqeq
                if (!this.isMultiSelecting && selRow && actObj && selRow.dataKey != actObj.id) {
                    return;
                }

                await this._validRowOnChange(evt.detail.data.newRowFirstSelect, ct);
                this._setActualObject(actObj);

                if (evt.detail.data.doubleClick &&
                    this.isLovFrame &&
                    this.ownerForm.enableLovSelect != false) {
                    this.selectLovValue(evt.detail.rowID);
                }

                if (this.ownerForm.detailFrame && this.ownerForm.detailFrame.element) {
                    this.ownerForm.detailFrame.element.classList.add("isSelectedObject");
                }

                if (await this._getClassEncryptingAllowed()) {
                    this.actualObject.objectDecrypted.add(() => {
                        if (this.isEditing) {
                            if (selRow instanceof IEditableDataRowElement)
                                selRow.isInEdit = true;
                        }
                    });
                }
            } catch (err) {
                if (err instanceof CancelError) // načítání bylo zrušeno (typicky výběrem dalšího záznamu)
                    return;

                ErrorHandler.instance.handleError(err);
            } finally {
                if (!isNewRowFirstSelect) {
                    if (showLoaderOnDetail) {
                        this.detail.menu.hideMenuLoader();
                    } else {
                        this.menu.hideMenuLoader();
                    }
                }
            }
        }

        if (await this._getClassEncryptingAllowed()) {
            this._decryptItemAction.enabled = this.actualObject != null && this.actualObject.isClientSideEncrypted && !this.actualObject.isEncryptionFinalized;
        }
    }

    public override async prevRow(): Promise<void> {
        if (this.currentDataGrid.selectedRows.length > 0 && await this.ownerForm.checkAndSaveForm({ sourceFrame: this.isEditing && !(this.editStyle === EditStyleType.esMulti) ? null : this, forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })) {
            const oldInactive = !this.isActive;
            if (oldInactive)
                this.onlyListFrameInteractive(true);
            this.currentDataGrid.prevRecord();
            if (oldInactive)
                this.onlyListFrameInteractive(false);
        }
    }

    public override async nextRow(): Promise<void> {
        if (this.currentDataGrid.selectedRows.length > 0 && await this.ownerForm.checkAndSaveForm({ sourceFrame: this.isEditing && !(this.editStyle == EditStyleType.esMulti) ? null : this, forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })) {
            const oldInactive = !this.isActive;
            if (oldInactive)
                this.onlyListFrameInteractive(true);

            this.currentDataGrid.nextRecord();
            if (this.currentDataGrid.keyPressRowIndex + 1 == this.currentDataGrid.dataRows.length)
                this.currentDataGrid.dataSource.getNextData(this.currentDataGrid.startIndex, this.currentDataGrid.fetchCount, false);

            if (oldInactive)
                this.onlyListFrameInteractive(false);
        }
    }

    public override canBeEnabled(actionType: ActionType): boolean {
        if (actionType === ActionType.standardPreviousRecord) {
            return this.currentDataGrid.keyPressRowIndex !== 0;
        } else if (actionType === ActionType.standardNextRecord) {
            return this.currentDataGrid.keyPressRowIndex + 1 !== this.currentDataGrid.dataRows.length;
        }

        throw new Error("ActionType může být pouze standardPreviousRecord nebo standardNextRecord");
    }

    /**
     * Odznačí vybrané záznamy na všech data gridech
     */
    public clearSelectedRows(): void {
        if (this.dataGrid) {
            this.dataGrid.clearSelectedRows();
        }

        if (this.compactDataGrid) {
            this.compactDataGrid.clearSelectedRows();
        }

        this.clearActualObject();
    }

    /**
     * Nastaví aktuálně používaný objekt
     * @param actObj Nově nastavovaný objekt
     * @param isFromMasterObjChanged Zda je vyvoláno změnou mastera
     */
    protected override _setActualObject(actObj: OWObject, isFromMasterObjChanged: boolean = false): void {
        if (this.actualObject !== actObj) {
            this._enabledInEditActions = null;
        }

        super._setActualObject(actObj, isFromMasterObjChanged);
    }

    /**
     * Reaguje na změnu stavu editovatelnosti
     * @param _sender Kdo si změnu žádá
     * @param _value Jaký je požadovaný stav
     */
    protected override async _editModeChanged(_sender: GenericListFrame, _value: boolean): Promise<void> {
        // Nechceme tu vůbec na změnu stavu reagovat
    }

    /** Vyčistí nastavení profilu a vrátí seznam do původního stavu */
    public override clearProfileInfo(): void {
        super.clearProfileInfo();

        if (this._groupGrid)
            this._removeGroupTree();

        this._updateQueryMode();
    }

    protected override async _setProfileInfo(info: IFrameProfileInfo): Promise<void> {
        await super._setProfileInfo(info);

        if (this._groupGrid)
            this._removeGroupTree();

        if (!this.profileInfo.attributesToGroup || this.disableGrouping)
            return;

        for (const attrToGroup of this.profileInfo.attributesToGroup) {
            const column = this.dataGrid.columns.find(item => StringUtils.compareCaseInsensitive(item.filterAttrName, attrToGroup));
            if (!column) {
                // pokud datagrid neobsahuje sloupec uvedený v profilu, groupování neaplikujeme a zavřeme groupovací panel
                if (this._groupGrid)
                    this._removeGroupTree();

                return;
            }

            await this._updateGroupTreeAttributes(column, false);
        }
    }

    /** Provede aktualizaci stavu QueryMode */
    private async _updateQueryMode(): Promise<boolean> {
        if (this._shouldApplyQueryMode()) {
            if (this._groupGrid) {
                this._groupGrid.clearDataRows();
            }

            this.setToQueryMode(() => this._queryModeDataLoadMethod());
            return true;
        }

        if (this._isInQueryMode()) {
            await this._queryModeDataLoadMethod();
            return true;
        }

        return false;
    }

    /**
     * Metoda načtení dat po ukončení Query módu. Vymaže aktivní filtr.
     */
    private _queryModeDataLoadMethod(): Promise<void> {
        this._queryModeFinished = true;

        if (this.dataGrid instanceof EditableDataGrid) {
            this.dataGrid.unregisterLoadDataQueryMode();
        }

        // vymazeme jen filtr, ktery nebyl nastaven v profilu
        if (this.profileInfo.filterId != this.filterManager.currentFilter?.filterId) {
            this.filterManager?.clearFilter();
        } else {
            if (this.profileInfo.filterId) {
                this.filterManager.execute();
                return Promise.resolve();
            }
        }

        // pro případ, kdy má seznam nastavený DefaultGroup
        if (!this.disableGrouping && this._groupGrid)
            this._loadGroupTreeData();

        return this.refreshOnClick();
    }

    /** Provede refresh po změně profilu, umožňuje-li to nastavení QueryMode */
    protected override async _profileChangeRefresh(hasNewlyAddedAttributes: boolean): Promise<void> {
        if (await this._updateQueryMode()) {
            return;
        }

        await super._profileChangeRefresh(hasNewlyAddedAttributes);
    }

    /** Zda má být použit režim QueryMode */
    protected _shouldApplyQueryMode(): boolean {
        return !this.masterFrame && this.ownerForm.queryMode;
    }

    /** Spustí načtení dat na základě zviditelnění sloupce */
    protected override _columnVisibilityDataLoad(): void {
        if (this._isInQueryMode()) {
            return;
        }

        super._columnVisibilityDataLoad();
    }

    /**
     * Je volána při změně master objektu
     * @param masterObjectChangedArgs Obsahuje předchozí a nový objekt
     */
    protected override async _onMasterObjectChanged(masterObjectChangedArgs?: IActualObjectChangedEventArgs): Promise<void> {
        if (!ControlUtils.isElementVisible(this.element)) {
            // Promaže data na neviditelných ListFramech (na neaktivních pages)
            if (this.dataGrid) {
                this.dataGrid.clearSelectedRows();
            }
            if (this.compactDataGrid) {
                this.compactDataGrid.clearDataRows();
            }
            this.dataSource.clearData();

            this._resolveActualObjectSetDeferred(true);
            return;
        }

        await this.waitForRecordsSaved();
        await super._onMasterObjectChanged(masterObjectChangedArgs);

        let lastActualObject: OWObject | undefined;
        if (masterObjectChangedArgs && masterObjectChangedArgs.actualObject && masterObjectChangedArgs.previousActualObject
            && masterObjectChangedArgs.actualObject.id == masterObjectChangedArgs.previousActualObject.id && this.actualObject)
            lastActualObject = this.actualObject; // pokud zůstal master objekt stejný, zapamatujeme aktuální objekt a později ho znovu označíme
        else if (!masterObjectChangedArgs)
            lastActualObject = this.actualObject;
        if (masterObjectChangedArgs && !masterObjectChangedArgs.actualObject && masterObjectChangedArgs.previousActualObject) { // ulozime si pro budouci moznou potrebu
            this._previousActualObject = this.actualObject;
            this._previousMasterObject = masterObjectChangedArgs.previousActualObject;
        }

        if (this._pendingMasterObjectChange) {
            this._pendingMasterObjectChange = false;
        }

        await this._updateConditionAndRead(this.dataSource);
        await this.waitForDataLoad();

        if (lastActualObject) {
            if (this._lastSelectedRecordId == lastActualObject.id) this._lastSelectedRecordId = GenericListFrame._NO_ID;
            if (!(await this.selectRow(lastActualObject.id, true))) {
                await this.loadAndSelectRecord(lastActualObject.id);
            }
            if (this._previousActualObject) this._previousActualObject = null;
            if (this._previousMasterObject) this._previousMasterObject = null;
        }

        if (masterObjectChangedArgs && masterObjectChangedArgs.actualObject && this._previousMasterObject && masterObjectChangedArgs.actualObject.id == this._previousMasterObject.id) { // prijde novy actualObject, ale uz sme ztratili local informaci o predchozim objektu
            if (this._previousActualObject && !(await this.selectRow(this._previousActualObject.id, true))) {
                await this.loadAndSelectRecord(this._previousActualObject.id);
            }
            this._previousActualObject = null;
            this._previousMasterObject = null;
        }

        if (masterObjectChangedArgs && masterObjectChangedArgs.actualObject && masterObjectChangedArgs.previousActualObject
            && masterObjectChangedArgs.actualObject.id !== masterObjectChangedArgs.previousActualObject.id) {
            this._resolveActualObjectSetDeferred(true);
            this.clearSelectedRows(); // Změnil se master => je potřeba zrušit předchozí označení řádků
        }

        if (this.masterFrame.actualObject == null || (this.masterFrame.actualObject.isNew && !(this.masterFrame instanceof ISeparateDetailFrame))) {
            this.dataSource.clearData();
            if (this.dataGrid) {
                this.dataGrid.clearSelectedRows();
            }

            if (this.compactDataGrid) {
                this.compactDataGrid.clearDataRows();
            }

            if (this._groupGrid) {
                this._groupGrid.dataSource.condition = "1=0";
                this._groupGrid.dataSource.conditionParams = [];

                this._groupGrid.dataSource.clearData();
                this._groupGrid.clearDataRows();
            }

            this.cancelChanges();
        } else if (this._groupGrid) {
            // Nechceme použít condition z DS, protože už obsahuje omezující podmínky ze stromu. Zároveň nechceme ani this.condition, protože neobsahuje omezující vazby
            await this._createConditionAndConditionParams(this._groupGrid.dataSource);
            this.refreshGroupTree(true);
        }

        this._filterManager.frameCondition = await this.createReaderCondition();

        if (masterObjectChangedArgs && masterObjectChangedArgs.previousActualObject) {
            masterObjectChangedArgs.previousActualObject.attributesChanged.remove(this._masterObjectAttrsChangedListener);
        }

        if (this.masterObject) {
            this.masterObject.attributesChanged.add(this._masterObjectAttrsChangedListener = async (_sender, attributeNames) => {
                if (this.conditionParamsAttrs?.filter(attr => attributeNames.includes(attr)).length > 0) {
                    await this.updateConditionAndRead();
                }
            });
        }
    }

    /**
    * Kontrola validnosti zaznamu
    * @param checkRecommended jestli maji se kontrolovat doporucena pole
    * @param checkPostponedRequired jestli se maji konrolovat odlozene povinnosti
    * @param validatedUnchangedObjects jestli se ma kontrola provest i když není záznam změněný
    */
    public override async validate(checkRecommended?: boolean, checkPostponedRequired?: boolean, validatedUnchangedObjects?: boolean): Promise<boolean> {
        if (this.detail) {
            await this.detail.waitForPendingActions();
            await this.detail.checkEmptyComponents(checkRecommended, checkPostponedRequired, validatedUnchangedObjects);
        }
        if (this.visible) {
            await super.validate(checkRecommended);
        }
        // musi se zde pred vracenim vysledku znova provest waitForPendingUpdates,
        // protoze validace se sice provede a vyvola v predchozich updatech,
        // ale vola se asynchrone a je pridana do chainu tasku v pendingUpdates
        await this.waitForPendingUpdates();
        return !this.validationManager.hasErrors;
    }

    public override async validateAndShowMessage(): Promise<boolean> {
        if (await this.validate(false))
            return true;

        await SharedMethods.showValidationMessages(this.validationManager);
        return false;
    }

    public override async deleteRecord(maintainSelection: boolean = false): Promise<boolean> {
        // zkontroluje se uložení změn na masteru a jeho nadřízených framech
        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
            return false;

        const res = await this._confirmDeleteDialog();

        if (res.result === MessageButtonType.ok) {
            try {
                if (!await this.lockObject(this.actualObject))
                    return false;

                UIManager.instance.showLoader();

                if (this.useMasterContext) {
                    await this.createContext();
                    this.setToolbarToEditMode(true);
                }

                const resultMessages = await this.dataSource.deleteObject(this.actualObject.id, undefined, maintainSelection);

                this.clearActualObject();
                this._clearWorkflow();
                await this._doMasterRefresh();
                UIManager.instance.showResultMessages(resultMessages);

                this.refreshGroupTree(true);

                return true;
            } catch (err) {
                ErrorHandler.instance.handleError(err);
            } finally {
                UIManager.instance.hideLoader();
            }
        }

        return false;
    }

    protected _removeRecordFromGrid(): void {
        this.currentDataGrid.removeSelectedRows();
    }

    public override async editRecord(): Promise<boolean> {
        if (this.actualObject) {
            if (!await this.lockObject())
                return false;
            //nastaví flag, že je objekt rozeditován. Narozdíl od isChanged, ale nemusí mít změněnou žádnou hodnotu.
            this.editingRow.trigger(this, this.actualObject);
        }
        // zkontroluje se uložení změn na masteru a jeho nadřízených framech
        if (this.editStyle === EditStyleType.esSingle && !(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext, sourceFrame: this })))
            return false;
        if (this.editStyle === EditStyleType.esMulti && !(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext, sourceFrame: this })))
            return false;

        if (!this.isEditable || (this.isEditable && !this.enabled)) {
            if (this.detailForm)
                this._openDetailSeparately();
            else
                return false;
        } else {
            this.isEditing = true;
        }

        return true;
    }

    /**
     * Po kliknuti na Obnovit v toolbaru znovu docte data v seznamu
     */
    public override async refreshOnClick(): Promise<void> {
        await this.refreshData(true);
        await this.refreshGroupTree(true);
    }

    /**
     * Znovunacteni dat do seznamu
     * @param manualRefresh Jestli je akce vyvolana uzivatelem (zabranuje nebo povoleuju oznaceni radku v seznamu)
     */
    public override async refreshData(manualRefresh: boolean): Promise<void> {
        if (this._isDisposed || (this._isInQueryMode() && !manualRefresh))
            return;

        const yesnoActionFnc = async () => {
            this._lastSelectedRecordId = GenericListFrame._NO_ID;
            await this.updateCondition(this.dataSource);
            await this.dataSource.refreshData(undefined, manualRefresh, this._cancelPrevAndCreateDataLoadingCTS().token); // Loading dialog spuštěn na GenericListFrame
            await this._newEvaluateRightsGroup(ControlRightTypesGroup.selectionChanged);
        };

        if (this.isChanged) {
            await this.showSaveChangesDialog(yesnoActionFnc);
        } else {
            return yesnoActionFnc();
        }
    }

    protected override async _saveRecords(actualObjectOnly?: boolean): Promise<boolean> {
        try {
            this._recordsSavedDeferred = PromiseUtils.defer();
            const result = await this.saveRecords(actualObjectOnly ? this.actualObject.id : null);

            this.dataSaved.trigger(this, undefined);

            return (result === null);
        } catch (e) {
            ErrorHandler.instance.handleError(e);

            return false;
        } finally {
            this._recordsSavedDeferred.resolve();
        }
    }

    /**
     * Zpracování zpráv při ukladaní záznamů
     * @param resultMessage - zpravy ze sluzby pri ukladani zaznamu
     */
    private _showValidationMessages(resultMessage: SaveResultData[]): Promise<SaveResultData[]> {
        return SharedMethods.processSaveResultsAndShowMessages(this, resultMessage);
    }

    /**
     * Navratove hodnoty:
     * null - ulozeni probehlo v poradku
     * prazdne pole - ulozeni se nepodarilo, ale neni co zpracovat
     * naplene pole - vraci vyjimky, vznikle pri ukladani
     * @param id Pokud je vyplněn, uloží pouze jeden záznam. V opačném případě ukládá všechny rozeditované objekty.
     * @param checkRecommended zda se mají kontrolovat doporučená pole
     * @param checkPostponed zda se mají kontrolovat odložená pole
     * @param skipValidation Pokud je true, tak klient nebude validovat pole
     * @param isClosing Příznak zdali se zavírá formulář
     * @param skipSavingEditContextId Příznak zdali se má přeskočit ukládání editačního kontextu
     */
    public override async saveRecords(id?: string, checkRecommended?: boolean, checkPostponed?: boolean, skipValidation?: boolean, isClosing?: boolean, skipSavingEditContextId?: boolean): Promise<SaveResultData[] | null> {
        await super.saveRecords();
        // Může dojít k nesouběhu eventů a nemusel by se pak správně posílat s applyUpdates editační kontext, proto může být nutné kontext založit
        if (this.isEditing && this.isChanged) {
            await this.createContext();
        }

        // Je potreba provest a pockat na akce pred ulozenim
        const beforeSaveAll: Array<Promise<void>> = [];
        this.componentTree.traverseDFS((control) => {
            if (control.control instanceof IBeforeSaveAwaitable) {
                beforeSaveAll.push(control.control.beforeSave());
            }
        });

        await Promise.all(beforeSaveAll);

        // pred ukladanim je potreba pockat na mozne probihajici updaty
        await this.waitForPendingUpdates();

        // stav před začátkem ukládání - pokud už byl seznam v readonly, tak jeho stav neměníme (třeba když je z něj otevřený modální detailový formulář, který spouští uložení)
        // jinak se přepne do readonly jen na chvíli, než doběhne ukládání záznamů
        const savingWithoutChanges = !this.isChanged;

        const wasMultiEditing = this.isMultiEditing;
        try {
            if (UploadManager.instance.isUploadRunningOnClass(this.dataSource.className)) {
                await MessageWindow.showAlert(new LocalizedStringBuilder("GUI-601245")); // Probíhá upload souborů
                return [];
            }

            let clientValidationFail = false;
            if (!skipValidation) {
                if (!(await this.validate(checkRecommended, checkPostponed, this.isEditContextInitiator))) {
                    clientValidationFail = true;
                }
            }

            UIManager.instance.showLoader();

            if (wasMultiEditing) {
                this.dataSource.requiredAttrs = this.detail.requiredAttrs;
            }
            const result = await this.dataSource.saveObjects(id ? [id + ""] : undefined, skipValidation, CancellationToken.none); //+"" protože se objekt předával jako number a nešel porovnat pomocí indexOf

            if (result.length === 1 && result[0]?.data) {
                // Při uložení jednoho záznamu aktualizuje `lastSelectedRecordID` kvůli změně `dataKey`. Více info v metodě.
                this._updateLastSelectedRecordID(result[0].data);
            }

            if (result.some(m => m.isInLongTransaction)) {
                new MessageWindow(new LocalizedStringBuilder("GUI-719276")).show(); // Ukládání bylo přesunuto na pozadí
                this.isEditing = false;
                this.isChanged = false;
                return [];
            }

            let validationErrorMessages = await this._showValidationMessages(result);

            // TODO Prazdne pole chybovych validacnich hlasek nam rika, ze validace probehla uspesne - viz this._showValidationMessages.
            // Pokud bysme ale vraceli prazdne pole, mechanismus volajici tuto metodu by prazdne pole povazoval jako chybu a je tedy nutne navratovou hodnotu prenastavit na null, coz je zpracovano jako uspesne ulozeny zaznam.
            // V budoucnu bude nutne projit vsechny vyskyty, kde jsou prazdna pole zpracovavana.
            // Mohlo by se totiz stat, ze chybne vracime prazdne pole, i kdyz vsechno probehlo uspesne, vcetne ulozeni. Viz task https://tfs.tescosw.loc/Jadro/Jadro%20.NET/_workitems/edit/33956
            if (validationErrorMessages?.length === 0) {
                validationErrorMessages = null;
            }

            if (await SharedMethods.checkObjectBeforeExecute(this.validationManager, "GUI-626119", "GUI-33412", skipValidation) === ObjectCheckingResult.recommendedContinue) { //"GUI-626119"=Pokračovat,"GUI-33412"=Zrušit)
                this.validationManager.clearErrors();
                return await this.saveRecords(id, false);
            }

            if (validationErrorMessages && validationErrorMessages.length > 0) {
                if (validationErrorMessages[0]?.data?.id) {
                    this.dataGrid.selectRow(validationErrorMessages[0].data.id);
                }
                return validationErrorMessages;
            }

            if (!skipValidation && (clientValidationFail || this.dataSource.editedObjectsCount > 0)) {
                return [{ data: this.actualObject, errors: this.actualObject.getValidationErrors().map((error) => error.error), isInLongTransaction: result.some(m => m.isInLongTransaction) }];
            }

            // Může se spustit refresh a s ním YNC dialog na podřízených, nevalidních framech
            let savingFramesBeforeContextFailed = false;

            if (!skipSavingEditContextId && this.isEditContextInitiator) {
                if (await this.ownerForm.checkAndSaveForm({ skipSavingEditContextId: true, forceSave: true, sourceFrame: this, openedModalFrames: this.openedModalMasterableFrames })) {
                    await this.saveContext();

                    if (wasMultiEditing) {
                        this.dataSource.refreshData();
                    }
                } else {
                    savingFramesBeforeContextFailed = true;
                }
            }

            if (!this.isEditContextInitiator) {
                this.isEditing = false;
                this.isChanged = false;

                if (!isClosing && !savingFramesBeforeContextFailed) {
                    await this._doMasterRefresh();
                    this.actualObjectSaved.trigger(this, this.actualObject);
                    this.refreshGroupTree(true);
                }
            }

            return validationErrorMessages;
        } finally {
            if (savingWithoutChanges)
                this.unlockObject();

            if (id == null && this.isMultiSelecting && !this.isEditContextInitiator) {
                //this.isEditing = false;
                //this.isChanged = false;
                //await this.dataSource.refreshData(null, true);
                //this.validationManager.clearErrors();
                this.cancelChanges();
            }

            UIManager.instance.hideLoader();
        }
    }

    /**
     * Umožňuje počkat na dokončení ukládání
     * @returns Pokud je ukládání dokončené, vrátí true, jinak false
     */
    public async waitForRecordsSaved(): Promise<boolean> {
        return this._recordsSavedDeferred ? this._recordsSavedDeferred.promise : Promise.resolve(false);
    }

    /**
     * Vytvoří kopii záznamu
     * @param propagateError na true znamená, že chyby si ošetří volající metoda (např. na DetailFramu)
     */
    public override async createCopy(propagateError?: boolean): Promise<boolean> {
        // Zkontroluje se uložení změn na masteru a jeho nadřízených framech
        try {
            if (await this.lockObject())
                await this.unlockObject();
            else
                return false;

            if (this.currentDataGrid.selectedRows.length == 0 || !(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
                return false;

            if (this._shouldOpenDetailInModal)
                this._openDetailInNewWindow = true;

            this._clearWorkflow();

            await this.createContext();

            if (this._showOnMainForm && this.ownerForm.detailFrame == null) {
                this._openDetailSeparately({
                    openMode: OpenModeType.insert,
                    objID: this.actualObject?.instanceID,
                });
            } else {
                const newRow = await this.dataSource.copyRow(this.currentDataGrid.selectedRows[0].dataKey, undefined, await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), false));
                await this._validRowOnChange();
                this._setActualObject(newRow.actualObject);
            }

            if (!this._shouldOpenDetailInModal) {
                // Před vstupem do editace je potřeba počkat na vyhodnocení práv
                await this.waitForActualObjectSet();
                await this.rightsEvaluationTask;
                this.isEditing = true;
            }
            this.isChanged = true;

            return true;
        } catch (exception) {
            if (propagateError) {
                throw exception;
            }

            ErrorHandler.instance.handleError(exception);
        }

        return false;
    }

    public override async cancelChanges(maintainSelection: boolean = false): Promise<void> {
        await this.cancelPendingActions();

        if (!await this.unlockObject())
            return;

        const resetEditing = () => {
            this.isEditing = false;
            this.isChanged = false;
        };

        if (!this.isEditContextInitiator) {
            resetEditing();
        }

        UploadManager.instance.stopUploads(this.dataSource.className);

        if (!this.isEditContextInitiator) {
            if (this.detail)
                await this.detail.setDetailToEditMode(false, this.interactive);
        }

        let rollbackedContext = false;
        /**
         * Pokud ruším editační kontext, je nutné zahodit změny i na podřízených framech
         * !!! Je nutné předat sourceFrame, aby nedošlo k zacyklení při Form._cancelChangesOnFrames !!!
        */
        if (this.isEditContextInitiator) {
            if (await this.ownerForm.checkAndSaveForm({ forceSave: true, skipSavingEditContextId: true, cancelChanges: true, sourceFrame: this, openedModalFrames: this.openedModalMasterableFrames })) {
                await this.rollbackContext();
                rollbackedContext = true;
            }
        }

        await this.dataSource.cancelChanges("", false, maintainSelection);

        // po zrušení změn se odstraňují všechny existujícíc validační chyby
        this.validationManager.clearErrors();

        if (rollbackedContext) {
            resetEditing();
        }

        if (this.currentDataGrid) {
            if (this.currentDataGrid.selectedRows.length === 0) {
                this._setActualObject(null);
            } else {
                const selectedRowDataKey = this.currentDataGrid.selectedRows[0].dataKey;

                if (rollbackedContext || this.actualObject.isChanged || (!(this.actualObject instanceof IMultiEditObject) && selectedRowDataKey != this.actualObject.id)) {
                    const obj = await this.getActualObject(selectedRowDataKey, true, this.objectName, this._actualObjectCTS.token);
                    this._setActualObject(obj);

                    if (!obj) {
                        this.currentDataGrid.clearSelectedRows();
                        this._clearWorkflow();
                    }
                }

                // Na LF mohly zůstat záznamy, které byly v kontextu zahozeny
                if (rollbackedContext) {
                    await this.dataSource.refreshData(null, true);
                }
            }
        } else {
            this._setActualObject(null);
        }
    }

    /**
     * Zruseni zmen na radku
     * @param dataKey Identifikátor datového záznamu
     */
    private async _cancelRowChanges(dataKey: string): Promise<void> {
        const row = this.currentDataGrid.dataRows.find(r => r.dataKey == dataKey);
        if (!row)
            throw new InvalidOperationException(`Row with dataKey ${dataKey} not found`); //`

        if (this.onCancelMethod) {
            try {
                if (this.currentDataGrid.selectedRows.length === 1 && dataKey == this.currentDataGrid.selectedRows[0].dataKey) {
                    await this.unlockObject();
                } else {
                    const obj = await this.getActualObject(dataKey, true, this.objectName, this._actualObjectCTS.token);
                    if (!obj.isNew)
                        await this.unlockObject(obj);
                }
            } catch (err) {
                ErrorHandler.instance.handleError(err);
                return;
            }
        }

        // pokud na rušeném záznamu probíhá upload, tak ho je potřeba zrušit
        UploadManager.instance.stopUploads(this.dataSource.className, dataKey);

        await this._cancelPendingTaskOnRow(dataKey);
        this.validationManager.clearErrors(dataKey);

        if (this.currentDataGrid.selectedRows.length === 1 && dataKey == this.currentDataGrid.selectedRows[0].dataKey) {
            try {
                await this.dataSource.cancelChanges(dataKey.toString(), true);
            } catch (e) {
                ErrorHandler.instance.handleError(e);
            }

            // actual object se nacita pouze tehdy pokud radek zustal po zruseni v seznamu

            if (row.isAttached) {
                const obj = await this.getActualObject(dataKey, true, this.objectName, this._actualObjectCTS.token);
                if (this.currentDataGrid.selectedRows.length > 0) {
                    if (row instanceof IEditableListDataRowElement)
                        row.setInstance(obj);
                    this._setActualObject(obj);
                }
            }
        } else {
            try {
                await this.dataSource.cancelChanges(dataKey.toString());
            } catch (e) {
                ErrorHandler.instance.handleError(e);
            }
        }

        if (row instanceof IEditableListDataRowElement)
            row.isChanged = false;
        this.recordChangesCanceled.trigger(this, dataKey);

        if (this.isEditContextInitiator) {
            await this.ownerForm.checkAndSaveForm({ forceSave: true, skipSavingEditContextId: true, cancelChanges: true, sourceFrame: this });
            await this.rollbackContext();
        }

        if (this.dataSource.editedObjectsCount === 0) {
            if (this.detail) {
                await this.detail.setDetailToEditMode(false, this.interactive);
            }

            if (!this.validationManager.hasErrors) {
                this.isEditing = false;
            } else {
                this.setToolbarToEditMode(false);
            }

            this.isChanged = false;

            //může nastat situace, že uživatel rozdedituje několik záznamů, ale ve skutečnosti žádný nezmění. Když pak u jednoho zavolá zrušit změny, tak toolbar vypadne z editace a spolu s tím je potřeba dát vědět, že v podstatě došlo ke zrušení všech změn.
            this.changesCanceled.trigger(this, undefined);
            return;
        }
        if (this.actualObject == undefined && this.isEditing)
            this.dataGrid.editedRows[this.dataGrid.editedRows.length - 1].select();
    }

    protected override _createOrGetRightsOverrides(): ListFrameRightsEvaluationOverrides {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new ListFrameRightsEvaluationOverrides(this);

        return this.__rightsEvaluationOverrides;
    }

    public override async newRecord(objectName?: string, skipCheckSaveOnMaster: boolean = false): Promise<boolean> {
        try {
            // kontrola a případné uložení nadřízených framů, Pokud dam v YNC dialogu neukladat na nove zalozenem zaznamu tak se zaznam smaze a podrizeny frame nema MasterObject
            if (!skipCheckSaveOnMaster && !((this.editStyle == EditStyleType.esMulti) && (this.isEditing))) { //
                if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext }))) {
                    return false;
                }
            }
            if (this.masterFrame && this.masterObject == null) {
                return false;
            }

            // CheckAndSaveForm() mohlo vyvolat nějaké dočítání dat na podřízených framech - je potřeba počkat, až doběhnou existující refreshe
            await this.dataSource.waitForReload();

            if (this._shouldOpenDetailInModal && !FrameSharedMethods.isFormNameImplicit(this.detailForm))
                this._openDetailInNewWindow = true;

            if (this.currentDataGrid && this.currentDataGrid.selectedRows.length > 1) {
                const firstSelect = this.currentDataGrid.selectedRows[0];
                this.currentDataGrid.clearSelectedRows();
                firstSelect.select();
            }

            this._clearWorkflow();

            await this.createContext();

            if (this._showOnMainForm && this.ownerForm.detailFrame == null) {
                this._openDetailSeparately({
                    openMode: OpenModeType.insert,
                });
            } else {
                await this.dataSource.newRow(await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), true), objectName); // pridavani noveho sloupce ridi udalost z DS na DG
            }

            if (this.dataGrid.dataRows.length === 1) {
                /* Pokud byl seznam prázdný, nebylo podle čeho určit správnou šířku prvního sloupce s kontrolními prvky. Proto je třeba
                 * při vytvoření prvního záznamu tuto šířku aktualizovat. Potřebuje ji agregační řádek. */
                this._triggerComputedChoiceCellWidthChanged();
            }

            await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED);

            const setEditingState = async () => {
                if (this._openDetailInNewWindow) {
                    this.isEditing = false;
                } else if (this.isEditable && ((this.masterLinkToAttr && this.masterFrame) || (!this.masterFrame && !this.detail)) || this.editStyle == EditStyleType.esMulti) { // přepnutí do stavu editace - podmínka je dost zvláštní, ale složena z toho, co tu už bylo - neručím za ni, nebylo to komentováno
                    this.isEditing = true; // pro přepnutí do editace musí být vyhodnocené a povolené právo na editaci
                }
                await this._newEvaluateRights([RightType.visible, ...RightGroups.getControlRightGroupTypes(ControlRightTypesGroup.inEdit)]);
            };

            // TODO-rites: is really necessary?
            const setEditingStatePredicate: () => boolean = () => {
                return this.actualObject && this.actualObject.isNew;
            };

            if (setEditingStatePredicate())
                await setEditingState();
            else
                this.actualObjectSet.addPredicateLimitedListener(async () => {
                    if (setEditingStatePredicate())
                        await setEditingState();
                },
                setEditingStatePredicate);
            // TODO-rites: is really necessary? END

            this.isChanged = true;
            if (this.currentDataGrid instanceof IListDataGrid)
                this.currentDataGrid.scrollToSelectedRow();
        } catch (err) {
            ErrorHandler.instance.handleError(err);
            return false;
        }

        return true;
    }

    /**
     * Vytvoří WF akce a vloží je do toolbaru při hromadném výběru záznamů
     */
    private async _createMultiWorkflowActions(): Promise<void> {
        if (this.hideButtons.workflow) return;

        // Pokud některý z označených řádků není uložený, tzn. pracuje pouze s instanceID,
        // vrátíme prázdné pole, protože průnik WF těchto řádků je prázdný.
        if (this.currentDataGrid.selectedRows.some(row => ObjectUtils.isIdTemporary(row.dataKey))) {
            return;
        }

        try {
            UIManager.instance.showLoader();

            const ids: string[] = this.currentDataGrid.selectedRows.map(row => row.dataKey);
            if (ids.length < 1)
                return;
            else if (ids.length === 1) { // pro situaci, kdy je označených více záznamů a poté se označí jen jeden
                FrameSharedMethods.clearWorkflowItems(this._wfMultiActions, this.menu);
                return;
            }

            let items: IWorkflowMenuItem[] = [];
            if (ids.length > 1) {
                const objList = this.createSelectionObjectList();

                items = await OWManager.objectsWFMenu(objList, this._getRequestContext());
                if (items.length === 0) { // pokud pro záznamy neexistují společné WF akce, dojde k promazání akcí v toolbaru a návratu z metody
                    FrameSharedMethods.clearWorkflowItems(this._wfMultiActions, this.menu);
                    return;
                }

                const wfMultiItems = await FrameSharedMethods.createMultiWorkflowItems(items, this, this.ownerForm, objList, Array.from(this._wfMultiActions));
                // pokud nebyly přidány žádné nové MultiWF akce, nenastavuje se do 'this._wfMultiActions' nic,
                // protože akce, které tam byly přidané behěm souběhu při rychlém označení záznamů by už nešly odebrat
                if (wfMultiItems.size > 0)
                    this._wfMultiActions = wfMultiItems;
            }

            this.workflowItemsChanged.trigger(this, items);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Znovu načte položky WF pro actualObject.
     */
    public override async reloadWFItems(): Promise<void> {
        if (this.currentDataGrid.selectedRows.length > 0) {
            await this._reloadMultiWFItems();
            return;
        }
        if (this.actualObject != null) {
            await this._getWFItems();
        }
    }

    /**
     * Reloads WF entries when selecting multiple records.
     */
    private async _reloadMultiWFItems(): Promise<void> {
        if (!this._keyboardModifiersWatcher.ctrl && !this._keyboardModifiersWatcher.shift) {
            FrameSharedMethods.clearWorkflowItems(this._wfMultiActions, this.menu);
            await this._createMultiWorkflowActions();

            return;
        }

        if (!this._keyboardModifiersWatcher.ctrlChanged.containsListener(this._createMultiWorkflowActionsListener)) {
            this._keyboardModifiersWatcher.ctrlChanged.add(this._createMultiWorkflowActionsListener);
        }

        if (!this._keyboardModifiersWatcher.shiftChanged.containsListener(this._createMultiWorkflowActionsListener)) {
            this._keyboardModifiersWatcher.shiftChanged.add(this._createMultiWorkflowActionsListener);
        }
    }

    private _createMultiWorkflowActionsListener = () => {
        if (this._keyboardModifiersWatcher.ctrl || this._keyboardModifiersWatcher.shift) {
            return Promise.resolve([]);
        }
        this._keyboardModifiersWatcher.ctrlChanged.remove(this._createMultiWorkflowActionsListener);
        this._keyboardModifiersWatcher.shiftChanged.remove(this._createMultiWorkflowActionsListener);

        FrameSharedMethods.clearWorkflowItems(this._wfMultiActions, this.menu);
        return this._createMultiWorkflowActions();
    };

    /**
     * Vytvoří filtr na základě podmínek z datasource a příznaku pro čtení verzovaných záznamů.
     * @private
     */
    private _createFilter(): Filter {
        return {
            readVersioned: this.readVersioned,
            condition: this.dataSource.condition,
            conditionParams: this.dataSource.conditionParams,
        };
    }

    /**
     * Zkontroluje, zda agregační řádek používá aktuální filtr a pokud ne, tak vyvolá událost pro jeho aktualizaci.
     * @return Vrací true, pokud byla událost vyvolána. Jinak vrací false.
     * @private
     */
    private _triggerFilterUpdatedIfNeeded(): boolean {
        const newFilter = this._createFilter();

        if (!this._aggregationRowFilter || !areFiltersEqual(newFilter, this._aggregationRowFilter)) {
            this.filterChanged.trigger(this, {
                filter: newFilter,
            });
            this._aggregationRowFilter = newFilter;
            return true;
        }

        return false;
    }

    ////#endregion Methods

    //#region IDisposable
    //destruktor
    public override dispose(): void {
        if (this._isDisposed) {
            return;
        }
        super.dispose();

        this.rollbackContext();

        this.filterExecuted.removeAll();
        this.columnWidthChanged.removeAll();
        this.orderOfColumnsChanged.removeAll();
        this.columnDefinitionsChanged.removeAll();
        this.columnAdded.removeAll();
        this.columnRemoved.removeAll();
        this.filterChanged.removeAll();
        this.itemsRefreshed.removeAll();

        this.__rightsEvaluationOverrides?.dispose();

        this._aggregationRowAction?.dispose();
        this._aggregationRowConnector?.dispose();

        window.removeEventListener("resize", this._eventFunction["resize"]);
        window.removeEventListener("resizecomponent", this._eventFunction["resizecomponent"]);
        this.canValueLoadChanged.remove(this._canValueLoadChangedListener);
        UIManager.instance.windowResized.remove(this._windowResizedListener);

        this.masterFrame?.selectionChanged?.remove(this._selectionChangedListener);
        this.masterFrame?.actualObjectChanged?.remove(this._addObjectChangedListener);
        this.masterFrame?.actualObject?.attributeChanged?.remove(this._newObjectIdChangedListener);
        (this.dataSource as IMEDataSource)?.isMultiEditingChanged.remove(this._isMultiEditingChangedListener);
        this.dataSource?.dispose();

        if (this.masterObject) {
            this.masterObject.attributesChanged.remove(this._masterObjectAttrsChangedListener);
        }
        this._isMultiEditingChangedListener = null;
        this._selectionChangedListener = null;
        this._groupSelectionChangedListener = null;
        this._masterObjectAttrsChangedListener = null;

        this.dataGrid?.dispose();
        this.compactDataGrid?.dispose();

        this._filterManager?.dispose();
        this._filterMenu?.dispose();
        this._filterAction?.dispose();
        this._filterListAction?.dispose();

        this._groupGrid?.dispose();
        this._groupColumns = [];
        this._fulltextAttributes = [];
    }

    protected override _dropdownMenuShow(e: CustomEvent): void {
        const groupingItem = this._headerDropDown.querySelector(".menu-grouping-item");

        if (groupingItem) {
            if ((e.target as Column).allowGrouping === true) {
                (groupingItem as DropDownMenuItem).style.display = "flex";
            } else {
                (groupingItem as DropDownMenuItem).style.display = "none";
            }
        }
        super._dropdownMenuShow(e);
    }

    //#endregion IDisposable
} //end ListFrame

export enum ListFrameGridStyle {
    gsauto,
    gsgrid,
    gscompact,
}
